import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { ToastContainer, ToastItem } from './ToastContainer';
import { initializeApp, getApps, getApp, deleteApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signInAnonymously, 
  signOut, 
  onAuthStateChanged, 
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  linkWithCredential
} from "firebase/auth";
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager,
  memoryLocalCache,
  getFirestore,
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  runTransaction, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  limit,
  setLogLevel,
  enableNetwork
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { GoogleGenAI } from "@google/genai";
import { safeStringify, safeSanitize } from './src/utils/safeJson';

// --- PRODUCTION CONFIGURATION ---
// Firebase App Hosting automatically handles the origin, so we default to relative paths
const IS_PROD = (import.meta as any).env?.PROD; 

export const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || ''; 

export const getTodayDateString = (d: Date = new Date()): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const normalizeDate = (d: string): string => {
  if (!d) return '';
  const trimmed = d.trim();
  if (trimmed.includes('-')) {
    const parts = trimmed.split('-');
    if (parts.length === 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
    }
  } else if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
      } else {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }
  }
  return trimmed;
};

export const normalizeTime = (t: string): string => {
  if (!t) return '';
  let trimmed = t.trim();
  const isPM = /pm/i.test(trimmed);
  const isAM = /am/i.test(trimmed);
  trimmed = trimmed.replace(/\s*(am|pm)/i, '').trim();
  const parts = trimmed.split(':');
  if (parts.length >= 2) {
    let hour = parseInt(parts[0], 10);
    const minute = parts[1].padStart(2, '0').slice(0, 2);
    if (isPM && hour < 12) hour += 12;
    if (isAM && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  }
  return trimmed;
};

export const parseRecordTimestamp = (record: any): number => {
  if (!record) return 0;
  if (typeof record.timestamp === 'number' && !isNaN(record.timestamp)) return record.timestamp;
  if (record.timestamp && typeof record.timestamp.seconds === 'number') return record.timestamp.seconds * 1000;
  
  if (record.createdAt) {
    const t = Date.parse(record.createdAt);
    if (!isNaN(t)) return t;
  }
  
  if (record.issueDate) {
    const t = Date.parse(record.issueDate);
    if (!isNaN(t)) return t;
  }

  if (record.date) {
    const raw = String(record.date).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const t = Date.parse(raw);
      if (!isNaN(t)) return t;
    }
    const parts = raw.split(/[/.-]/).map(p => parseInt(p.trim(), 10));
    if (parts.length === 3 && parts.every(p => !isNaN(p))) {
      let year = parts[0];
      let month = parts[1];
      let day = parts[2];
      if (parts[2] > 31) {
        year = parts[2];
        if (parts[0] > 12) {
          day = parts[0];
          month = parts[1];
        } else {
          month = parts[0];
          day = parts[1];
        }
      }
      const d = new Date(year, month - 1, day);
      if (!isNaN(d.getTime())) return d.getTime();
    }
    const fallback = Date.parse(raw);
    if (!isNaN(fallback)) return fallback;
  }
  return 0;
};

const firebaseConfig = {
  apiKey: (import.meta as any).env?.VITE_FIREBASE_API_KEY || "AIzaSyDspDusuMTkWXeWzW70sOvPLgGumxJ1Z4s",
  authDomain: (import.meta as any).env?.VITE_FIREBASE_AUTH_DOMAIN || "rovita-production.firebaseapp.com",
  projectId: (import.meta as any).env?.VITE_FIREBASE_PROJECT_ID || "rovita-production",
  storageBucket: (import.meta as any).env?.VITE_FIREBASE_STORAGE_BUCKET || "rovita-production.firebasestorage.app",
  messagingSenderId: (import.meta as any).env?.VITE_FIREBASE_MESSAGING_SENDER_ID || "80508688453",
  appId: (import.meta as any).env?.VITE_FIREBASE_APP_ID || "1:80508688453:web:788e24759cd435c671eb2c",
  measurementId: (import.meta as any).env?.VITE_FIREBASE_MEASUREMENT_ID || "G-D7BWTEL3XL"
};

let app: any;
let auth: any;
let db: any;
let storage: any;
let analytics: any;

try {
    // Silence transient offline warnings in sandbox and preview environments
    setLogLevel('silent');
    if (!getApps().length) {
        app = initializeApp(firebaseConfig);
    } else {
        app = getApp();
    }
    auth = getAuth(app);
    try {
        db = initializeFirestore(app, {
          experimentalAutoDetectLongPolling: true,
          localCache: persistentLocalCache({
            tabManager: persistentMultipleTabManager()
          })
        });
    } catch (e1) {
        try {
            db = initializeFirestore(app, {
              experimentalAutoDetectLongPolling: true,
              localCache: memoryLocalCache()
            });
        } catch (e2) {
            try {
                db = initializeFirestore(app, {
                  experimentalAutoDetectLongPolling: true
                });
            } catch (e3) {
                db = getFirestore(app);
            }
        }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (db) enableNetwork(db).catch(() => {});
      });
    }
    storage = getStorage(app);
    isSupported().then(yes => {
      if (yes && app) analytics = getAnalytics(app);
    }).catch(() => {});
} catch (e) {
    console.error("Firebase Initialization Error", e);
}

export type Role = 'patient' | 'doctor' | 'admin' | 'guest';

// Helper to remove undefined fields and prevent circular structure errors in Firestore/logging
export const sanitizeData = (data: any): any => safeSanitize(data);

export interface WorkingDay {
  start: string;
  end: string;
  active: boolean;
  allowSpecialRequests?: boolean;
}

export interface WorkingHours {
  [key: string]: WorkingDay;
}

export const DEFAULT_WORKING_HOURS: WorkingHours = {
  'Monday': { start: '09:00', end: '17:00', active: true },
  'Tuesday': { start: '09:00', end: '17:00', active: true },
  'Wednesday': { start: '09:00', end: '17:00', active: true },
  'Thursday': { start: '09:00', end: '17:00', active: true },
  'Friday': { start: '09:00', end: '17:00', active: true },
  'Saturday': { start: '09:00', end: '13:00', active: false },
  'Sunday': { start: '09:00', end: '13:00', active: false },
};

export interface User {
  id: string;
  mrn?: string; // Medical Record Number
  name: string;
  email: string;
  role: Role;
  created_at: string;
  isAnonymous?: boolean;
  avatar?: string;
  specialties?: string[];
  bio?: string;
  isVerified?: boolean;
  isDisabled?: boolean;
  disabled?: boolean;
  status?: 'active' | 'revoked' | 'suspended' | string;
  age?: string;
  gender?: string;
  bloodType?: string;
  registeredId?: string;
  dob?: string;
  guardianName?: string;
  guardianPhone?: string;
  isMinor?: boolean;
  phone?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  locationVerifiedAt?: string;
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
  pastMedicalHistory?: string;
  currentMedications?: string;
  allergies?: string;
  socialHistory?: string;
  informedConsent?: boolean;
  weight?: string;
  height?: string;
  lastBP?: string;
  lastHeartRate?: string;
  lastTemp?: string;
  lastGlucose?: string;
  credentialUrl?: string;
  medicalLicenseNumber?: string;
  licenseNumber?: string; // Official board/state medical registration number
  rovitaLicenseId?: string; // Humane platform practitioner identifier (e.g. ROV-MD-74291)
  workingHours?: WorkingHours;
  timezone?: string; // Added timezone support
  sessionRate?: number; // Added sessionRate in $
  allowSpecialRequests?: boolean; // Doctor setting: allow special request slots on non-working days
  busySlots?: { date: string; time: string; reason?: string; type?: 'busy' | 'open' }[]; // Doctor specific blocked or custom open slots
  isOnline?: boolean; // Real-time user online presence
  lastSeen?: number | string; // Timestamp of last activity
}

/**
 * Generates a clean, professional, and humane Rovita License / Telehealth Practitioner ID
 * Avoids ugly random UUIDs or robotic strings.
 */
export const formatHumaneRovitaLicenseId = (u?: Partial<User> | null, seed?: string): string => {
  if (u?.licenseNumber && u.licenseNumber.trim()) {
    return u.licenseNumber.trim();
  }
  if (u?.rovitaLicenseId && u.rovitaLicenseId.startsWith('ROV-')) {
    return u.rovitaLicenseId;
  }
  const idSeed = u?.id || seed || 'ROVITA';
  let hash = 0;
  for (let i = 0; i < idSeed.length; i++) {
    hash = (hash * 31 + idSeed.charCodeAt(i)) % 90000;
  }
  const cleanNum = (10000 + Math.abs(hash)).toString().slice(0, 5);
  const prefix = u?.role === 'doctor' ? 'ROV-MD' : 'ROV-LIC';
  return `${prefix}-${cleanNum}`;
};

/**
 * Checks whether any user account is disabled, suspended, or revoked by admin
 */
export const isUserDisabled = (u?: Partial<User> | null): boolean => {
  if (!u) return false;
  return Boolean(u.isDisabled || u.disabled || u.status === 'suspended' || u.status === 'revoked');
};

/**
 * Checks whether a doctor is active, verified, and not disabled/suspended
 */
export const isDoctorActive = (u?: Partial<User> | null): boolean => {
  if (!u) return false;
  if (u.role !== 'doctor') return false;
  return !isUserDisabled(u);
};

export const DEFAULT_SPECIALISTS: User[] = [
  {
    id: 'doc_yousuf',
    name: 'Dr. Mohammad Yousuf Awan',
    email: 'dryousuf@rovita.co',
    role: 'doctor',
    created_at: '2025-01-01T00:00:00.000Z',
    isVerified: true,
    avatar: '/Dr.Yousuf.png',
    specialties: ['Internal Medicine', 'Geriatrics', 'Acute Care'],
    bio: 'Founder & CEO. Consultant General Internal Medicine, Acute Medicine, and Geriatrics (UK). Fellow of the American College of Physicians (FACP), CESR/CCST certified, Member Academy of Medical Educators (UK). Over 25 years of global inpatient and ambulatory clinical leadership.',
    sessionRate: 50,
    timezone: 'UTC',
    licenseNumber: 'GMC-4829104',
    rovitaLicenseId: 'ROV-MD-10101',
    workingHours: DEFAULT_WORKING_HOURS
  },
  {
    id: 'doc_rameesha',
    name: 'Dr. Rameesha Awan',
    email: 'drrameesha@rovita.co',
    role: 'doctor',
    created_at: '2025-01-01T00:00:00.000Z',
    isVerified: true,
    avatar: '/Rameesha.jpeg',
    specialties: ['General Medicine', 'Preventive Care', 'Family Health'],
    bio: 'Chief Medical Officer at ROVITA. MBBS (UK), Member Advisory Board specializing in holistic telemedicine protocols, preventive primary care, chronic disease management, and patient-first virtual consultations.',
    sessionRate: 45,
    timezone: 'UTC',
    licenseNumber: 'GMC-7392018',
    rovitaLicenseId: 'ROV-MD-10102',
    workingHours: DEFAULT_WORKING_HOURS
  },
  {
    id: 'doc_arslan',
    name: 'Dr. Ahmad Arslan',
    email: 'drarslan@rovita.co',
    role: 'doctor',
    created_at: '2025-01-01T00:00:00.000Z',
    isVerified: true,
    avatar: '/Dr. Arslan.jpeg',
    specialties: ['Nephrology', 'Internal Medicine', 'Kidney Health'],
    bio: 'Consultant Nephrologist and Director of Nephrology, Baylor College of Medicine. US Board Certified in Internal Medicine & Nephrology (MBBS, MD USA). Specializes in hypertension, electrolyte disorders, and renal diagnostics.',
    sessionRate: 75,
    timezone: 'UTC',
    licenseNumber: 'TX-MD-610294',
    rovitaLicenseId: 'ROV-MD-10103',
    workingHours: DEFAULT_WORKING_HOURS
  },
  {
    id: 'doc_armoghan',
    name: 'Dr. Ahmad Armoghan',
    email: 'drarmoghan@rovita.co',
    role: 'doctor',
    created_at: '2025-01-01T00:00:00.000Z',
    isVerified: true,
    avatar: '/Dr. Armoghan.jpeg',
    specialties: ['Cardiology', 'Interventional Cardiology', 'Vascular Medicine'],
    bio: 'Interventional Cardiologist, Lake Charles Memorial Hospital. US Board Certified in Cardiovascular Disease, Interventional Cardiology, Nuclear Cardiology, and Endovascular Medicine (FACC, FSCAI, FSVM, RPVI).',
    sessionRate: 85,
    timezone: 'UTC',
    licenseNumber: 'LA-MD-592817',
    rovitaLicenseId: 'ROV-MD-10104',
    workingHours: DEFAULT_WORKING_HOURS
  }
];

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  exchangeRateFromUSD: number;
  exchangeRateFromPKR: number;
  standardFeeOriginal: number;
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  PKR: { code: 'PKR', symbol: 'Rs.', name: 'Pakistani Rupee', exchangeRateFromUSD: 280, exchangeRateFromPKR: 1, standardFeeOriginal: 1000 },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', exchangeRateFromUSD: 1, exchangeRateFromPKR: 0.0036, standardFeeOriginal: 50 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', exchangeRateFromUSD: 0.79, exchangeRateFromPKR: 0.0028, standardFeeOriginal: 40 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', exchangeRateFromUSD: 0.92, exchangeRateFromPKR: 0.0033, standardFeeOriginal: 45 },
  CAD: { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', exchangeRateFromUSD: 1.36, exchangeRateFromPKR: 0.0049, standardFeeOriginal: 70 },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', exchangeRateFromUSD: 1.52, exchangeRateFromPKR: 0.0054, standardFeeOriginal: 75 },
  AED: { code: 'AED', symbol: 'AED', name: 'UAE Dirham', exchangeRateFromUSD: 3.67, exchangeRateFromPKR: 0.013, standardFeeOriginal: 180 },
  SAR: { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', exchangeRateFromUSD: 3.75, exchangeRateFromPKR: 0.0134, standardFeeOriginal: 185 },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', exchangeRateFromUSD: 84, exchangeRateFromPKR: 0.30, standardFeeOriginal: 4000 }
};

export interface FormattedPrice {
  currencyCode: string;
  currencySymbol: string;
  originalCutoff: string;
  displayPrice: string;
  isFree: boolean;
  rawDisplayAmount: number;
}

export interface TriageQuestionnaire {
  completedAt: string;
  chiefComplaint: string;
  symptomDuration: string;
  severity: number; // 1-10 scale
  associatedSymptoms?: string[];
  currentMedications?: string;
  knownAllergies?: string;
  pastMedicalConditions?: string[];
  isUrgentFlagged?: boolean;
  notes?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachments?: { name: string; url: string; size?: number; type?: string }[];
}

export interface Appointment {
  id: string;
  patientId: string;
  doctorId: string;
  doctorName: string;
  doctorEmail?: string;
  patientName: string;
  patientEmail?: string;
  date: string; // Stored in Doctor's Timezone
  time: string; // Stored in Doctor's Timezone
  timeZoneId?: string; // The Doctor's Timezone ID (e.g. "America/New_York")
  status: 'upcoming' | 'in-progress' | 'completed' | 'cancelled';
  notes?: string;
  privateNotes?: string; // Doctor's confidential clinical notes
  triage?: TriageQuestionnaire; // Pre-consultation patient triage responses
  callStartedAt?: number; // Epoch timestamp of when session timer began
  totalDurationSeconds?: number; // Total allocated seconds (e.g., 1800 + extensions)
  cancelReason?: string;
  referralTo?: string;
  startTime?: string;
  endTime?: string;
  communicationMethod?: 'video' | 'audio' | 'text';
  isFree?: boolean; // Flag to skip payments for follow-ups
  feeAmount?: number;
  feeCurrency?: string;
  paymentStatus?: 'paid' | 'complimentary' | 'refunded' | 'pending';
  paymentIntentId?: string;
  refundAmount?: number;
  refundedAt?: string;
  reminderSent?: boolean;
  reminderSentAt?: string;
  patientOnline?: boolean;
}

export interface PrescriptionItem {
  id?: string;
  name: string;
  dosage: string;
  form?: string;
  frequency: string;
  duration: string;
  instructions?: string;
  dispenseQuantity?: string;
}

export interface MedicalRecordEditHistory {
  editedAt: string;
  editedByDoctorId: string;
  editedByDoctorName: string;
  summaryOfChanges?: string;
  previousValues: Record<string, any>;
}

export interface MedicalRecord {
  id: string;
  patientId: string;
  doctorId: string;
  doctorName: string;
  date: string;
  authoredBy: 'doctor' | 'patient';
  
  // Clinical Fields
  presentingSymptoms?: string;
  historyOfPresentIllness?: string;
  pastMedicalHistory?: string;
  socialHistory?: string;
  vitalSigns?: string;
  physicalExamFindings?: string;
  labReports?: string;
  imagingStudies?: string;
  diagnosis: string;
  treatmentPlan?: string;
  prescription: string;
  prescriptionItems?: PrescriptionItem[];
  rxId?: string;
  issuedAt?: string;
  doctorSpecialty?: string;
  doctorLicenseNumber?: string;
  patientName?: string;
  patientMrn?: string;
  patientDob?: string;
  patientGender?: string;
  patientAge?: string;
  patientAddress?: string;
  patientBloodGroup?: string;
  patientAllergies?: string;
  dietaryAdvice?: string;
  generalPrecautions?: string;
  followUpInstructions?: string;
  referralsIssued?: string;
  notes: string;
  privateNotes?: string;
  appointmentId?: string;
  createdAt?: string;
  timestamp?: number;
  
  // Attachments
  attachmentUrl?: string;
  attachmentName?: string;
  attachments?: { name: string; url: string; size?: number; type?: string; }[];

  // In-Call Chat Transcript
  chatTranscript?: {
    id?: string;
    senderId: string;
    senderName?: string;
    text: string;
    timestamp: string;
  }[];

  // Audit and Modification History
  editHistory?: MedicalRecordEditHistory[];
  lastEditedAt?: string;
  lastEditedBy?: string;
  lastEditedById?: string;
}

export interface AppNotification {
  id: string;
  userId: string;
  message: string;
  type: 'appointment' | 'system' | 'medical';
  timestamp: string;
}

export interface AuditLog {
  id: string;
  action: string;
  userId: string;
  userName: string;
  timestamp: string;
}

export interface ContactMessage {
  id: string;
  name: string;
  surname?: string;
  email: string;
  phone?: string;
  isRegistered?: boolean;
  mrNumber?: string;
  message: string;
  timestamp: string;
  status: 'new' | 'read';
  repliedAt?: string;
  replyMessage?: string;
}

export interface Review {
  id: string;
  doctorId: string;
  patientId: string;
  patientName: string;
  rating: number; // 1-5
  comment: string;
  timestamp: string;
}

export interface GlobalSettings {
  paymentsEnabled: boolean;
  complimentaryAccessEnabled: boolean;
  aiEngineActive: boolean;
  maintenanceMode: boolean;
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  platformName: string;
  platformCurrency: string;
  stripePublicKey?: string;
  stripeSecretKey?: string;
  paypalClientId?: string;
  paypalSecret?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpUser?: string;
  smtpPass?: string;
}

interface AppContextType {
  user: User | null;
  loading: boolean;
  doctors: User[];
  doctorsError: string | null;
  allUsers: User[];
  appointments: Appointment[];
  allAppointments: Appointment[];
  medicalRecords: MedicalRecord[];
  notifications: AppNotification[];
  auditLogs: AuditLog[];
  contactMessages: ContactMessage[];
  reviews: Review[];
  globalSettings: GlobalSettings;
  userCurrency: string;
  userCountry: string;
  toasts: ToastItem[];
  showToast: (title: string, message: string, type?: 'success' | 'info' | 'warning' | 'error') => void;
  removeToast: (id: string) => void;
  setUserCurrency: (currency: string) => void;
  formatDoctorPrice: (sessionRateUSD?: number) => FormattedPrice;
  toggleDoctorBusySlot: (date: string, time: string, reason?: string, slotType?: 'busy' | 'open') => Promise<void>;
  setDoctorDayBusy: (date: string, isBusy: boolean, slotType?: 'busy' | 'open') => Promise<void>;
  login: (e: string, p: string) => Promise<void>;
  loginGuest: () => Promise<void>;
  logout: () => void;
  register: (n: string, e: string, p: string, r: Role, extras: { dob: string, phone: string, gender: string, address: string, guardianName?: string, guardianPhone?: string }) => Promise<void>;
  adminCreateUser: (e: string, p: string, n: string, r: Role) => Promise<void>;
  bookAppointment: (doctorId: string, time: string, date: string, doctorTimezone: string, isFree?: boolean, paymentInfo?: { feeAmount: number; feeCurrency: string; paymentStatus?: 'paid' | 'complimentary'; paymentIntentId?: string }) => Promise<void>;
  updateAppointmentStatus: (id: string, status: 'in-progress' | 'completed' | 'cancelled') => Promise<void>;
  cancelAppointment: (id: string, reason: string) => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  manageUser: (id: string, data: Partial<User>) => Promise<void>;
  toggleUserDisabled: (id: string) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  resetUserPassword: (email: string) => Promise<void>;
  changeUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  adminChangeUserPassword: (userId: string, newPassword: string, sendResetEmail?: boolean) => Promise<void>;
  addMedicalRecord: (record: Omit<MedicalRecord, 'id'>) => Promise<void>;
  updateMedicalRecord: (recordId: string, updatedFields: Partial<MedicalRecord>, changeReason?: string) => Promise<void>;
  deleteMedicalRecord: (id: string) => Promise<void>;
  issueDigitalPrescription: (data: {
    patientId: string;
    patientName: string;
    patientMrn?: string;
    patientAge?: string;
    patientGender?: string;
    patientAllergies?: string;
    diagnosis: string;
    items: PrescriptionItem[];
    rawPrescriptionText?: string;
    treatmentPlan?: string;
    dietaryAdvice?: string;
    generalPrecautions?: string;
    followUpInstructions?: string;
    notes?: string;
    appointmentId?: string;
  }) => Promise<string>;
  uploadClinicalFile: (file: File) => Promise<{ url: string, name: string }>;
  uploadPublicFile: (file: File) => Promise<{ url: string, name: string }>;
  submitTriageQuestionnaire: (appointmentId: string, triageData: TriageQuestionnaire) => Promise<void>;
  clearNotification: (id: string) => Promise<void>;
  askClinicalAI: (prompt: string) => Promise<string>;
  saveGlobalSettings: (settings: Partial<GlobalSettings>) => Promise<void>;
  adminUpdateAppointment: (id: string, data: Partial<Appointment>) => Promise<void>;
  adminDeleteAppointment: (id: string) => Promise<void>;
  scheduleFollowUp: (patientId: string, doctorId: string, date: string, time: string, timezone: string) => Promise<void>;
  referPatient: (patientId: string, toDoctorId: string, notes: string) => Promise<void>;
  sendContactMessage: (data: { name: string, surname: string, email: string, phone: string, message: string, isRegistered: boolean, mrNumber?: string }) => Promise<void>;
  replyToContactMessage: (id: string, email: string, reply: string) => Promise<void>;
  updateContactMessage: (id: string, status: 'new' | 'read') => Promise<void>;
  deleteContactMessage: (id: string) => Promise<void>;
  submitReview: (doctorId: string, rating: number, comment: string) => Promise<void>;
  createPaymentIntent: (amount: number) => Promise<string>;
  sendSystemEmail: (to: string, subject: string, body: string) => Promise<void>;
  linkGuestAccount: (email: string, password: string, name?: string) => Promise<void>;
  loginDoctorGuest: (doctorName?: string, specialty?: string) => Promise<void>;
  loginAdminGuest: (adminName?: string) => Promise<void>;
  switchUserRoleDemo: (newRole: Role) => void;
  checkAndDispatchAppointmentReminders: () => Promise<void>;
  testSmtpConfig: (config: { smtpHost?: string; smtpPort?: string; smtpUser?: string; smtpPass?: string; testRecipient?: string }) => Promise<{ success: boolean; message?: string; error?: string; guidance?: string }>;
}

export const getSafeUrl = (url?: string): string => {
  if (!url || typeof url !== 'string') return '#';
  const trimmed = url.trim();
  // Disallow javascript:, vbscript:, and data:text/html payloads
  if (/^(javascript|vbscript|data:text\/html):/i.test(trimmed)) {
    return '#';
  }
  // Allow safe web links, blob: URLs, and verified data image/pdf formats
  if (/^(https?:|blob:|data:(image\/(jpeg|png|webp|gif)|application\/pdf);base64,)/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return trimmed;
  }
  return '#';
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      if (!auth.currentUser && auth) {
        try {
          await signInAnonymously(auth);
        } catch (anonErr) {
          console.debug("Anonymous auth initialization:", anonErr);
        }
      }
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      if (user?.role) headers['X-User-Role'] = user.role;
      if (user?.email) headers['X-User-Email'] = user.email;
      if (user?.id) headers['X-User-Id'] = user.id;
    } catch (e) {
      console.debug("Auth token acquisition:", e);
    }
    return headers;
  };
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState<User[]>(DEFAULT_SPECIALISTS);
  const [doctorsError, setDoctorsError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]);
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const contactDedupeRef = useRef<Map<string, number>>(new Map());
  const [userIp, setUserIp] = useState<string | null>(null);
  const [userCountry, setUserCountry] = useState<string>('PK');
  const [userCurrency, setUserCurrencyState] = useState<string>('PKR');
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>({
    paymentsEnabled: true, complimentaryAccessEnabled: true, aiEngineActive: true, maintenanceMode: false, stripeEnabled: false, paypalEnabled: false, platformName: "ROVITA", platformCurrency: "USD"
  });

  const showToast = (title: string, message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info') => {
    const id = Date.now().toString() + Math.random().toString(36).substring(2, 6);
    const newToast: ToastItem = { id, title, message, type, timestamp: Date.now() };
    setToasts(prev => [...prev.slice(-4), newToast]);
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Network Disconnection & Reconnection Toast Notifications
  useEffect(() => {
    const handleOnline = () => {
      showToast('Network Reconnected', 'You are back online. Connectivity re-established.', 'success');
    };
    const handleOffline = () => {
      showToast('Network Disconnected', 'Network connection was lost. Operating in offline mode.', 'warning');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const setUserCurrency = (curr: string) => {
    if (CURRENCIES[curr]) {
      setUserCurrencyState(curr);
      try {
        localStorage.setItem('rovita_currency', curr);
      } catch (e) {}
    }
  };

  // Detect location and set currency
  useEffect(() => {
    const detectCurrencyAndCountry = async () => {
      let saved: string | null = null;
      try {
        saved = localStorage.getItem('rovita_currency');
      } catch (e) {}
      if (saved && CURRENCIES[saved]) {
        setUserCurrencyState(saved);
      }

      // Detect timezone & locale first (instant, synchronous)
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const lang = navigator.language || '';

        if (tz.includes('Karachi') || tz.includes('Islamabad') || lang.includes('PK') || lang.includes('ur')) {
          setUserCountry('Pakistan');
          if (!saved) setUserCurrencyState('PKR');
        } else if (tz.includes('London') || lang.includes('GB')) {
          setUserCountry('United Kingdom');
          if (!saved) setUserCurrencyState('GBP');
        } else if (tz.includes('Europe') || tz.includes('Berlin') || tz.includes('Paris') || tz.includes('Madrid') || tz.includes('Rome') || tz.includes('Amsterdam') || tz.includes('Dublin')) {
          setUserCountry('Europe');
          if (!saved) setUserCurrencyState('EUR');
        } else if (tz.includes('Toronto') || tz.includes('Vancouver') || tz.includes('Montreal') || lang.includes('CA')) {
          setUserCountry('Canada');
          if (!saved) setUserCurrencyState('CAD');
        } else if (tz.includes('Australia') || tz.includes('Sydney') || tz.includes('Melbourne') || lang.includes('AU')) {
          setUserCountry('Australia');
          if (!saved) setUserCurrencyState('AUD');
        } else if (tz.includes('Dubai') || tz.includes('Abu_Dhabi') || tz.includes('Muscat')) {
          setUserCountry('United Arab Emirates');
          if (!saved) setUserCurrencyState('AED');
        } else if (tz.includes('Riyadh')) {
          setUserCountry('Saudi Arabia');
          if (!saved) setUserCurrencyState('SAR');
        } else if (tz.includes('Kolkata') || tz.includes('Calcutta') || lang.includes('IN')) {
          setUserCountry('India');
          if (!saved) setUserCurrencyState('INR');
        } else {
          setUserCountry('United States');
          if (!saved) setUserCurrencyState('USD');
        }
      } catch (e) {}

      // Async IP country lookup fallback
      try {
        const res = await fetch('https://api.country.is/').catch(() => null);
        if (res && res.ok) {
          const data = await res.json();
          if (data && data.country) {
            const cCode = data.country.toUpperCase();
            if (cCode === 'PK') {
              setUserCountry('Pakistan');
              if (!saved) setUserCurrencyState('PKR');
            } else if (cCode === 'GB') {
              setUserCountry('United Kingdom');
              if (!saved) setUserCurrencyState('GBP');
            } else if (['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'IE', 'PT', 'GR', 'FI'].includes(cCode)) {
              setUserCountry('Europe');
              if (!saved) setUserCurrencyState('EUR');
            } else if (cCode === 'CA') {
              setUserCountry('Canada');
              if (!saved) setUserCurrencyState('CAD');
            } else if (cCode === 'AU' || cCode === 'NZ') {
              setUserCountry('Australia');
              if (!saved) setUserCurrencyState('AUD');
            } else if (cCode === 'AE') {
              setUserCountry('United Arab Emirates');
              if (!saved) setUserCurrencyState('AED');
            } else if (cCode === 'SA') {
              setUserCountry('Saudi Arabia');
              if (!saved) setUserCurrencyState('SAR');
            } else if (cCode === 'IN') {
              setUserCountry('India');
              if (!saved) setUserCurrencyState('INR');
            }
          }
        }
      } catch (e) {}
    };

    detectCurrencyAndCountry();
  }, []);

  const formatDoctorPrice = (sessionRateUSD?: number): FormattedPrice => {
    const curr = CURRENCIES[userCurrency] || CURRENCIES.PKR;
    const isGatewayOff = !globalSettings.paymentsEnabled;

    if (isGatewayOff) {
      if (userCurrency === 'PKR') {
        return {
          currencyCode: 'PKR',
          currencySymbol: 'Rs.',
          originalCutoff: 'Rs. 1,000',
          displayPrice: 'Rs. 0',
          isFree: true,
          rawDisplayAmount: 0
        };
      } else if (userCurrency === 'USD') {
        return {
          currencyCode: 'USD',
          currencySymbol: '$',
          originalCutoff: '$50.00',
          displayPrice: '$0',
          isFree: true,
          rawDisplayAmount: 0
        };
      } else {
        return {
          currencyCode: curr.code,
          currencySymbol: curr.symbol,
          originalCutoff: `${curr.symbol} ${curr.standardFeeOriginal}`,
          displayPrice: `${curr.symbol} 0`,
          isFree: true,
          rawDisplayAmount: 0
        };
      }
    }

    // Payments are active
    const baseRate = sessionRateUSD && sessionRateUSD > 0 ? sessionRateUSD : 50;
    if (userCurrency === 'PKR') {
      const pkrAmount = Math.round(baseRate * (curr.exchangeRateFromUSD || 280));
      return {
        currencyCode: 'PKR',
        currencySymbol: 'Rs.',
        originalCutoff: '',
        displayPrice: `Rs. ${pkrAmount.toLocaleString()}`,
        isFree: false,
        rawDisplayAmount: pkrAmount
      };
    }

    const converted = (baseRate * curr.exchangeRateFromUSD).toFixed(2);
    return {
      currencyCode: curr.code,
      currencySymbol: curr.symbol,
      originalCutoff: '',
      displayPrice: `${curr.symbol} ${converted}`,
      isFree: false,
      rawDisplayAmount: parseFloat(converted)
    };
  };

  const toggleDoctorBusySlot = async (date: string, time: string, reason?: string, slotType: 'busy' | 'open' = 'busy') => {
    if (!user || user.role !== 'doctor' || !db) return;
    const currentBusy = user.busySlots || [];
    const existsIndex = currentBusy.findIndex(b => b.date === date && b.time === time);
    let updatedBusy: { date: string; time: string; reason?: string; type?: 'busy' | 'open' }[];
    if (existsIndex >= 0) {
      const existing = currentBusy[existsIndex];
      const existingType = existing.type || 'busy';
      if (existingType === slotType) {
        // Toggle off
        updatedBusy = currentBusy.filter((_, idx) => idx !== existsIndex);
      } else {
        // Switch type (e.g. from busy to open or open to busy)
        updatedBusy = currentBusy.map((item, idx) => idx === existsIndex ? { ...item, type: slotType, reason: reason || (slotType === 'open' ? 'Custom open slot' : 'Doctor marked busy') } : item);
      }
    } else {
      updatedBusy = [...currentBusy, { date, time, reason: reason || (slotType === 'open' ? 'Custom open slot' : 'Doctor marked busy'), type: slotType }];
    }
    await updateDoc(doc(db, "users", user.id), { busySlots: updatedBusy });
    setUser(prev => prev ? { ...prev, busySlots: updatedBusy } : null);
    await logAction(`Doctor Slot Toggled: ${date} ${time} (${slotType})`);
  };

  const setDoctorDayBusy = async (date: string, isBusy: boolean, slotType: 'busy' | 'open' = 'busy') => {
    if (!user || user.role !== 'doctor' || !db) return;
    const currentBusy = user.busySlots || [];
    let updatedBusy: { date: string; time: string; reason?: string; type?: 'busy' | 'open' }[];
    if (isBusy) {
      const daySlots: { date: string; time: string; reason?: string; type?: 'busy' | 'open' }[] = [];
      for (let h = 8; h <= 19; h++) {
        const hh = h.toString().padStart(2, '0');
        daySlots.push({ date, time: `${hh}:00`, reason: slotType === 'open' ? 'Day Custom Open' : 'Day Blocked', type: slotType });
        daySlots.push({ date, time: `${hh}:30`, reason: slotType === 'open' ? 'Day Custom Open' : 'Day Blocked', type: slotType });
      }
      const filtered = currentBusy.filter(b => b.date !== date);
      updatedBusy = [...filtered, ...daySlots];
    } else {
      updatedBusy = currentBusy.filter(b => b.date !== date);
    }
    await updateDoc(doc(db, "users", user.id), { busySlots: updatedBusy });
    setUser(prev => prev ? { ...prev, busySlots: updatedBusy } : null);
    await logAction(`Doctor Day Slots Updated: ${date} (active: ${isBusy}, type: ${slotType})`);
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/whoami`)
      .then(res => res.json())
      .then(data => setUserIp(data.ip))
      .catch(err => console.debug("IP detection failed", err));
  }, []);

  const getClientMetadata = () => {
    try {
      const ua = navigator.userAgent;
      let browser = "Unknown";
      let os = "Unknown";

      if (ua.indexOf("Firefox") > -1) browser = "Firefox";
      else if (ua.indexOf("SamsungBrowser") > -1) browser = "Samsung Browser";
      else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) browser = "Opera";
      else if (ua.indexOf("Trident") > -1) browser = "Internet Explorer";
      else if (ua.indexOf("Edge") > -1) browser = "Edge";
      else if (ua.indexOf("Chrome") > -1) browser = "Chrome";
      else if (ua.indexOf("Safari") > -1) browser = "Safari";

      if (ua.indexOf("Win") > -1) os = "Windows";
      else if (ua.indexOf("Mac") > -1) os = "MacOS";
      else if (ua.indexOf("X11") > -1) os = "UNIX";
      else if (ua.indexOf("Linux") > -1) os = "Linux";
      else if (ua.indexOf("Android") > -1) os = "Android";
      else if (ua.indexOf("like Mac") > -1) os = "iOS";

      return { browser, os, userAgent: ua };
    } catch (e) {
      return { browser: 'Unknown', os: 'Unknown', userAgent: 'Unknown' };
    }
  };

  const logAction = async (action: string) => {
    if (!db || !user) return;
    try {
      const metadata = getClientMetadata();
      const uniqueId = `log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const logRef = doc(db, "audit_logs", uniqueId);
      await setDoc(logRef, { 
        action, 
        userId: user.id, 
        userName: user.name, 
        timestamp: new Date().toISOString(),
        ip: userIp || 'Unknown',
        ...metadata
      }, { merge: true });
    } catch(e) {
      console.debug("Log action notice:", e);
    }
  };

  // Helper: Generate Sequential MRN
  const getNextMRN = async (): Promise<string> => {
    if (!db) return `MRN-TEMP-${Date.now()}`;
    const counterRef = doc(db, "system", "counters");
    try {
        const mrnResult = await runTransaction(db, async (transaction) => {
            const sfDoc = await transaction.get(counterRef);
            let newCount = 1;
            if (sfDoc.exists()) {
                const data = sfDoc.data();
                newCount = (data.mrnSequence || data.mrn_count || 0) + 1;
            }
            
            // Format: MRN-00000001
            const mrn = `MRN-${newCount.toString().padStart(8, '0')}`;
            transaction.set(counterRef, { 
                mrnSequence: newCount, 
                mrn_count: newCount, 
                lastUpdated: new Date().toISOString() 
            }, { merge: true });
            return mrn;
        });
        return mrnResult;
    } catch (e: any) {
        if (e.message?.includes("database (default) does not exist")) {
            console.error("FIREBASE SETUP ERROR: The Firestore database named '(default)' has not been created in the Firebase console yet.");
        } else {
            console.error("MRN Sequence Error, falling back to random:", e);
        }
        return `MRN-${Math.floor(10000000 + Math.random() * 90000000).toString()}`;
    }
  };

  // --- AUTHENTICATION & INITIALIZATION ---
  useEffect(() => {
    let unsubUserDoc: (() => void) | null = null;
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
        if (unsubUserDoc) {
          unsubUserDoc();
          unsubUserDoc = null;
        }
        setLoading(true);
        try {
            if (authUser) {
                const docRef = doc(db, "users", authUser.uid);
                let docSnap;
                try {
                   docSnap = await getDoc(docRef);
                } catch (err) {
                   console.error("Profile Fetch Error:", err);
                }

                if (docSnap && docSnap.exists()) {
                  let userData = docSnap.data() as User;
                  if (userData.isDisabled || userData.disabled || userData.status === 'suspended' || userData.status === 'revoked') {
                    console.warn("User account is disabled/suspended by admin.");
                    await signOut(auth);
                    setUser(null);
                    showToast("Account Disabled", "Your account has been disabled by the clinic administrator. Please contact support.", "error");
                    return;
                  }
                  // MRN Backfill for Old Users
                  if (!userData.mrn) {
                      const newMrn = await getNextMRN();
                      await updateDoc(docRef, { mrn: newMrn });
                      userData.mrn = newMrn;
                  }
                  setUser({ id: authUser.uid, ...userData });
                } else if (authUser.isAnonymous) {
                  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  setUser({ 
                    id: authUser.uid, 
                    name: authUser.displayName || 'Guest Patient', 
                    email: authUser.email || 'guest@rovita.care', 
                    role: 'patient', 
                    created_at: new Date().toISOString(), 
                    isAnonymous: true, 
                    mrn: await getNextMRN(), 
                    avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=Guest-${authUser.uid.slice(0,4)}`,
                    timezone: browserTz
                  } as User);
                } else {
                  // Fallback: If document is still propagating or restricted, use Auth data
                  console.warn("User document not found or unreadable. Using auth fallback.");
                  const fallbackName = authUser.displayName || 'User';
                  setUser({
                    id: authUser.uid,
                    email: authUser.email || 'no-email',
                    name: fallbackName,
                    role: 'patient',
                    created_at: new Date().toISOString(),
                    mrn: `MRN-${Math.floor(10000000 + Math.random() * 90000000).toString()}`,
                    avatar: authUser.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(fallbackName)}`,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                  } as User);
                }

                // Attach real-time snapshot listener on authenticated user's document
                if (db && !authUser.isAnonymous) {
                  unsubUserDoc = onSnapshot(docRef, async (snap) => {
                    if (snap.exists()) {
                      const freshData = snap.data() as User;
                      if (freshData.isDisabled || freshData.disabled || freshData.status === 'suspended' || freshData.status === 'revoked') {
                        await signOut(auth);
                        setUser(null);
                        showToast("Account Disabled", "Your account has been disabled by the clinic administrator.", "error");
                        return;
                      }
                      setUser(prev => prev ? { ...prev, ...freshData, id: authUser.uid } : null);
                    }
                  }, (err) => {
                    console.debug("User real-time sync notice:", err);
                  });
                }
            } else {
                setUser(null);
            }
        } catch (e) {
            console.error("Critical Auth Error:", e);
            setUser(null);
        } finally {
            setLoading(false);
        }
    });

    return () => {
      unsubscribe();
      if (unsubUserDoc) unsubUserDoc();
    };
  }, []);

  // --- DATA STREAMS ---
  useEffect(() => {
    if (!db) return;
    return onSnapshot(doc(db, "system", "config"), (s) => {
        if (s.exists()) setGlobalSettings(s.data() as GlobalSettings);
    }, (err) => console.debug("Config stream restricted."));
  }, []);

  // Sync Reviews
  useEffect(() => {
    if (!db) return;
    const q = query(collection(db, "reviews"), orderBy("timestamp", "desc"), limit(100));
    return onSnapshot(q, (snapshot) => {
        setReviews(snapshot.docs.map(d => ({id: d.id, ...d.data()} as Review)));
    }, (err) => {
        console.debug("Reviews stream offline/pending:", err);
    });
  }, []);

  // Real-Time User Presence Heartbeat (Enables pre-call online check for patients and doctors)
  useEffect(() => {
    if (!db || !user?.id) return;

    const userDocRef = doc(db, 'users', user.id);
    let heartbeatTimer: any = null;

    const reportPresence = (online: boolean) => {
      try {
        updateDoc(userDocRef, {
          isOnline: online,
          lastSeen: Date.now()
        }).catch(() => {});
      } catch (_) {}
    };

    // Mark user online immediately
    reportPresence(true);

    // Heartbeat every 25 seconds while tab is active
    heartbeatTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        reportPresence(true);
      }
    }, 25000);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reportPresence(true);
      }
    };

    const onUserLeaving = () => {
      reportPresence(false);
    };

    window.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onUserLeaving);
    window.addEventListener('beforeunload', onUserLeaving);

    return () => {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      window.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onUserLeaving);
      window.removeEventListener('beforeunload', onUserLeaving);
      reportPresence(false);
    };
  }, [db, user?.id]);

  // Appointments Stream for Administrative Dashboard
  useEffect(() => {
    if (!db || user?.role !== 'admin') {
      if (!user || user.role !== 'admin') {
        setAllAppointments([]);
      }
      return;
    }
    const unsubApps = onSnapshot(query(collection(db, "appointments")), 
      (s) => setAllAppointments(s.docs.map(d => ({id: d.id, ...d.data()} as Appointment))), 
      (err) => console.debug("Admin appointments stream error:", err)
    );
    return () => unsubApps();
  }, [db, user?.role]);

  useEffect(() => {
    if (!db || !user) return;
    
    let unsubUsers = () => {};
    let unsubAudit = () => {};
    let unsubInquiries = () => {};
    let unsubAdminConfig = () => {};

    if (user.role === 'admin' || user.role === 'doctor') {
      unsubUsers = onSnapshot(query(collection(db, "users"), orderBy("created_at", "desc")), 
        (s) => setAllUsers(s.docs.map(d => ({id: d.id, ...d.data()} as User))), 
        (err) => console.debug("Users stream restricted.")
      );

      if (user.role === 'admin') {
        unsubAudit = onSnapshot(query(collection(db, "audit_logs"), orderBy("timestamp", "desc"), limit(100)), 
          (s) => setAuditLogs(s.docs.map(d => ({id: d.id, ...d.data()} as AuditLog))), 
          (err) => console.debug("Audit stream restricted.")
        );

        unsubInquiries = onSnapshot(query(collection(db, "inquiries"), orderBy("timestamp", "desc")), 
          (s) => setContactMessages(s.docs.map(d => ({id: d.id, ...d.data()} as ContactMessage))), 
          (err) => console.debug("Inquiries stream restricted.")
        );

        // Securely stream backend secrets into admin session only
        unsubAdminConfig = onSnapshot(doc(db, "system", "admin_config"), (s) => {
          if (s.exists()) {
            setGlobalSettings(prev => ({ ...prev, ...s.data() }));
          }
        }, (err) => console.debug("Admin config stream restricted."));
      }
    }

    return () => { unsubUsers(); unsubAudit(); unsubInquiries(); unsubAdminConfig(); };
  }, [user]);

  // Public Doctor Registry
  useEffect(() => {
    if (!db) return;
    const q = query(collection(db, "users"), where("role", "==", "doctor"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dbDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
      const activeDocs = dbDocs.filter(d => isDoctorActive(d));
      if (dbDocs.length > 0) {
        setDoctors(activeDocs);
      } else {
        setDoctors(DEFAULT_SPECIALISTS.filter(d => isDoctorActive(d)));
      }
      setDoctorsError(null);
    }, (error) => {
      console.warn("Firestore Registry Sync Notice:", error.code, error.message);
      // 'unavailable' signifies client offline mode or network latency; avoid flashing fatal error to users
      if (error.code !== 'permission-denied' && error.code !== 'unavailable') {
          setDoctorsError(error.message);
      }
    });
    return () => unsubscribe();
  }, []);

  // Synchronize doctors state whenever allUsers updates (e.g. admin toggles disabled status)
  useEffect(() => {
    if (allUsers.length > 0) {
      const disabledDocIds = new Set(
        allUsers.filter(u => u.role === 'doctor' && isUserDisabled(u)).map(u => u.id)
      );
      const disabledDocEmails = new Set(
        allUsers.filter(u => u.role === 'doctor' && isUserDisabled(u) && u.email).map(u => u.email!.toLowerCase())
      );
      setDoctors(prev => {
        const activeOnly = prev.filter(d => !disabledDocIds.has(d.id) && (!d.email || !disabledDocEmails.has(d.email.toLowerCase())));
        const activeInAllUsers = allUsers.filter(u => u.role === 'doctor' && isDoctorActive(u));
        const currentIds = new Set(activeOnly.map(d => d.id));
        const currentEmails = new Set(activeOnly.map(d => d.email?.toLowerCase()).filter(Boolean));
        const toAdd: User[] = [];
        for (const doc of activeInAllUsers) {
          if (!currentIds.has(doc.id) && (!doc.email || !currentEmails.has(doc.email.toLowerCase()))) {
            toAdd.push(doc);
          }
        }
        return toAdd.length > 0 ? [...activeOnly, ...toAdd] : activeOnly;
      });
    }
  }, [allUsers]);

  // Personal Data Streams
  useEffect(() => {
    if (!db || !user) return;
    const roleField = user.role === 'doctor' ? 'doctorId' : 'patientId';
    
    const unsubApp = onSnapshot(query(collection(db, "appointments"), where(roleField, "==", user.id)), 
        (s) => setAppointments(s.docs.map(d => ({id: d.id, ...d.data()} as Appointment))), 
        (err) => console.debug("Personal appointments restricted.")
    );
    
    const unsubRec = onSnapshot(query(collection(db, "records"), where(roleField, "==", user.id)), 
        (s) => {
          const list = s.docs.map(d => ({id: d.id, ...d.data()} as MedicalRecord));
          list.sort((a, b) => parseRecordTimestamp(b) - parseRecordTimestamp(a));
          setMedicalRecords(list);
        }, 
        (err) => console.debug("Personal records restricted.")
    );
    
    const unsubNotif = onSnapshot(query(collection(db, "notifications"), where("userId", "==", user.id)), 
        (s) => setNotifications(s.docs.map(d => ({id: d.id, ...d.data()} as AppNotification))), 
        (err) => console.debug("Notifications restricted.")
    );
    
    return () => { unsubApp(); unsubRec(); unsubNotif(); };
  }, [user]);

  const login = async (e: string, p: string) => { 
    const cred = await signInWithEmailAndPassword(auth, e, p);
    if (db && cred.user) {
      try {
        const uSnap = await getDoc(doc(db, "users", cred.user.uid));
        if (uSnap.exists()) {
          const uData = uSnap.data() as User;
          if (uData.isDisabled || uData.disabled || uData.status === 'suspended' || uData.status === 'revoked') {
            await signOut(auth);
            throw new Error("Your account has been disabled by the clinic administrator. Please contact support.");
          }
        }
      } catch (err: any) {
        if (err.message?.includes("disabled by the clinic administrator")) throw err;
      }
    }
    await logAction("Login"); 
  };
  const loginGuest = async () => { await signInAnonymously(auth); };
  
  const loginDoctorGuest = async (docName = 'Dr. Alex Rivera, MD', specialty = 'Internal Medicine & Cardiology') => {
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }
    const uid = auth.currentUser ? auth.currentUser.uid : 'doc-demo-uid';
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const demoDoctor: User = {
      id: uid,
      name: docName,
      email: 'alex.rivera@rovita.ai',
      role: 'doctor',
      created_at: new Date().toISOString(),
      isAnonymous: true,
      isVerified: true,
      mrn: 'DOC-84920194',
      sessionRate: 65,
      bio: 'Board-Certified Internal Medicine Specialist with over 12 years of clinical experience in preventive care, cardiology, and chronic disease management. Dedicated to high-standard, compassionate telemedicine consultations.',
      specialties: ['General Physician', 'Cardiology', 'Internal Medicine', 'Preventive Care'],
      workingHours: DEFAULT_WORKING_HOURS,
      allowSpecialRequests: true,
      phone: '+1 (555) 234-8900',
      gender: 'male',
      dob: '1984-06-15',
      address: '450 Clinical Way, Suite 300, Medical Plaza',
      avatar: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&q=80&w=400',
      timezone: browserTz,
      credentialUrl: 'https://example.com/license-verified.pdf'
    };
    
    if (db && auth.currentUser) {
      try {
        await setDoc(doc(db, "users", uid), sanitizeData(demoDoctor), { merge: true });
      } catch (e) {
        console.warn("Could not save demo doctor to firestore:", e);
      }
    }
    setUser(demoDoctor);
    await logAction("Doctor Guest Login");
  };

  const loginAdminGuest = async () => {
    showToast('Admin Credentials Required', 'Administrative access requires authenticating with your administrator email and password.', 'error');
    throw new Error('Administrative access requires authenticating with your administrator email and password.');
  };

  const switchUserRoleDemo = (newRole: Role) => {
    if (!user) return;
    // Privilege escalation protection: only users already authenticated as admin can switch views
    if (newRole === 'admin' && user.role !== 'admin') {
      showToast('Access Denied', 'Administrative privileges require authenticated administrator sign-in.', 'error');
      return;
    }
    if (user.role !== 'admin') {
      showToast('Action Restricted', 'Role switching is reserved for administrator preview accounts.', 'warning');
      return;
    }
    const updatedUser: User = {
      ...user,
      role: newRole,
      name: newRole === 'doctor' && !user.name.toLowerCase().startsWith('dr') ? `Dr. ${user.name}` : user.name,
      specialties: newRole === 'doctor' ? (user.specialties?.length ? user.specialties : ['General Physician', 'Internal Medicine']) : user.specialties
    };
    setUser(updatedUser);
  };

  const logout = () => { logAction("Logout"); signOut(auth); setUser(null); };

  const register = async (name: string, email: string, p: string, role: Role, extras: { dob: string, phone: string, gender: string, address: string, guardianName?: string, guardianPhone?: string }) => {
      const cred = await createUserWithEmailAndPassword(auth, email, p);
      if (cred.user) {
          await updateProfile(cred.user, { displayName: name });
      }
      
      const birthDate = new Date(extras.dob);
      const today = new Date();
      let ageCalculated = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          ageCalculated--;
      }

      const mrn = await getNextMRN();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userData: Partial<User> = { 
        name, 
        email, 
        role: role === 'doctor' ? 'doctor' : 'patient',
        isVerified: false,
        status: 'active',
        mrn, 
        created_at: new Date().toISOString(), 
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name)}`, 
        specialties: [],
        workingHours: role === 'doctor' ? DEFAULT_WORKING_HOURS : undefined,
        timezone: timezone,
        dob: extras.dob,
        phone: extras.phone,
        gender: extras.gender,
        address: extras.address,
        age: ageCalculated.toString(),
        isMinor: ageCalculated < 18,
        guardianName: extras.guardianName,
        guardianPhone: extras.guardianPhone
      };
      if (cred.user) {
          await setDoc(doc(db, "users", cred.user.uid), sanitizeData(userData));
          setUser({ id: cred.user.uid, ...userData } as User);
      }
      await logAction(`Register: ${role}`);
  };

  const adminCreateUser = async (email: string, p: string, name: string, role: Role) => {
      let secondaryApp;
      try {
          const apps = getApps();
          secondaryApp = apps.find(a => a.name === "secondary");
          if (!secondaryApp) {
              secondaryApp = initializeApp(firebaseConfig, "secondary");
          }
      } catch (e) {
          secondaryApp = initializeApp(firebaseConfig, "secondary");
      }
      
      const secondaryAuth = getAuth(secondaryApp);

      try {
          const cred = await createUserWithEmailAndPassword(secondaryAuth, email, p);
          if (cred.user) {
              const mrn = await getNextMRN();
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const userData = {
                  name, 
                  email, 
                  role,
                  mrn,
                  created_at: new Date().toISOString(),
                  avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name)}`,
                  isVerified: role === 'admin' ? true : false,
                  specialties: [],
                  workingHours: role === 'doctor' ? DEFAULT_WORKING_HOURS : undefined,
                  timezone: timezone
              };
              await setDoc(doc(db, "users", cred.user.uid), sanitizeData(userData));
          }
          await signOut(secondaryAuth);
          await logAction(`Admin Created User: ${email} (${role})`);
      } catch (e: any) {
          throw e;
      }
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (!user || !db) return;
    const cleanData = sanitizeData(data);
    await updateDoc(doc(db, "users", user.id), cleanData);
    setUser(prev => prev ? { ...prev, ...cleanData } : null);
    await logAction("Update Profile");
  };

  const manageUser = async (id: string, data: Partial<User>) => {
    const cleanData = sanitizeData(data);
    if ('isDisabled' in data || 'disabled' in data) {
      const isTargetDisabled = Boolean(data.isDisabled ?? data.disabled);
      cleanData.isDisabled = isTargetDisabled;
      cleanData.disabled = isTargetDisabled;
      cleanData.status = isTargetDisabled ? 'suspended' : 'active';
    }
    const target = allUsers.find(u => u.id === id) || doctors.find(d => d.id === id);
    if (target?.role && !cleanData.role) {
      cleanData.role = target.role;
    }
    if (target?.name && !cleanData.name) {
      cleanData.name = target.name;
    }
    if (target?.email && !cleanData.email) {
      cleanData.email = target.email;
    }

    if (db) {
      try {
        await updateDoc(doc(db, "users", id), cleanData);
      } catch (err) {
        console.warn("Firestore manageUser notice, attempting setDoc merge:", err);
        try {
          await setDoc(doc(db, "users", id), cleanData, { merge: true });
        } catch (setErr) {
          console.error("Firestore manageUser setDoc fallback failed:", setErr);
        }
      }
    }
    setAllUsers(prev => prev.map(u => u.id === id ? { ...u, ...cleanData } : u));

    // Synchronize doctors state immediately
    const isDoctorTarget = target?.role === 'doctor' || cleanData.role === 'doctor' || data.role === 'doctor';
    if (isDoctorTarget) {
      const isTargetDisabled = Boolean(cleanData.isDisabled || cleanData.disabled || cleanData.status === 'suspended' || cleanData.status === 'revoked');
      setDoctors(prev => {
        if (isTargetDisabled) {
          return prev.filter(d => d.id !== id && (!target?.email || d.email?.toLowerCase() !== target.email.toLowerCase()));
        } else {
          const existing = prev.find(d => d.id === id || (target?.email && d.email?.toLowerCase() === target.email.toLowerCase()));
          if (existing) {
            return prev.map(d => (d.id === id || (target?.email && d.email?.toLowerCase() === target.email.toLowerCase())) ? { ...d, ...cleanData, isDisabled: false, disabled: false, status: 'active' } : d);
          } else {
            const restored = { ...(target || {}), ...cleanData, id, isDisabled: false, disabled: false, status: 'active' } as User;
            return [...prev, restored];
          }
        }
      });
    }

    await logAction(`Admin Manage User: ${id} (${JSON.stringify(cleanData)})`);
  };

  const toggleUserDisabled = async (id: string) => {
    const target = allUsers.find(u => u.id === id) || doctors.find(d => d.id === id);
    const isCurrentlyDisabled = Boolean(target?.isDisabled || target?.disabled || target?.status === 'suspended' || target?.status === 'revoked');
    const nextDisabled = !isCurrentlyDisabled;
    await manageUser(id, {
      ...((target?.role === 'doctor' && target) ? {
        name: target.name,
        email: target.email,
        role: 'doctor',
        avatar: target.avatar,
        specialties: target.specialties,
        sessionRate: target.sessionRate,
        timezone: target.timezone,
        licenseNumber: target.licenseNumber,
        rovitaLicenseId: target.rovitaLicenseId,
        workingHours: target.workingHours,
        bio: target.bio
      } : {}),
      isDisabled: nextDisabled,
      disabled: nextDisabled,
      status: nextDisabled ? 'suspended' : 'active'
    });
    showToast(
      nextDisabled ? "User Disabled" : "User Enabled",
      `${target?.name || 'User'} has been ${nextDisabled ? 'disabled. Their account access has been suspended.' : 're-enabled successfully.'}`,
      nextDisabled ? "info" : "success"
    );
  };

  const deleteUser = async (id: string) => {
    if (!db) return;
    const target = allUsers.find(u => u.id === id) || doctors.find(d => d.id === id);
    try {
      await updateDoc(doc(db, "users", id), { status: 'revoked', disabled: true, isDisabled: true });
    } catch (e) {}
    try {
      await deleteDoc(doc(db, "users", id));
    } catch (e) {}
    setAllUsers(prev => prev.filter(u => u.id !== id));
    setDoctors(prev => prev.filter(d => d.id !== id && (!target?.email || d.email?.toLowerCase() !== target.email.toLowerCase())));
    await logAction(`Admin Delete User: ${id}`);
  };

  const changeUserPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    if (!auth.currentUser) {
      throw new Error("No active session found. Please log in first.");
    }
    if (auth.currentUser.isAnonymous || !auth.currentUser.email) {
      throw new Error("Guest demo accounts cannot change passwords. Please register a personal account.");
    }
    if (!newPassword || newPassword.length < 6) {
      throw new Error("New password must be at least 6 characters long.");
    }

    try {
      if (currentPassword) {
        const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
      }
      await updatePassword(auth.currentUser, newPassword);
      await logAction("Password Changed Successfully");

      if (user?.id) {
        try {
          await addDoc(collection(db, "notifications"), {
            userId: user.id,
            title: "Security Notice: Password Changed",
            message: "Your account password was updated successfully. If this wasn't you, contact support immediately.",
            read: false,
            createdAt: new Date().toISOString()
          });
        } catch (notifErr) {
          console.warn("Notice creation failed", notifErr);
        }
      }
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        throw new Error("Current password is incorrect. Please verify and try again.");
      } else if (err.code === 'auth/weak-password') {
        throw new Error("Password is too weak. Please use at least 6 characters with a combination of letters and numbers.");
      } else if (err.code === 'auth/requires-recent-login') {
        throw new Error("For security, please enter your current password or log out and back in to change your password.");
      } else {
        throw new Error(err.message || "Failed to update password. Please try again.");
      }
    }
  };

  const adminChangeUserPassword = async (userId: string, newPassword: string, sendResetEmail = true): Promise<void> => {
    if (!user || user.role !== 'admin') {
      throw new Error("Unauthorized: Only platform administrators can change user passwords.");
    }
    if (!newPassword || newPassword.length < 6) {
      throw new Error("New password must be at least 6 characters long.");
    }

    const targetUser = allUsers.find(u => u.id === userId);
    if (!targetUser) {
      throw new Error("Target user account not found in system directory.");
    }

    try {
      // If the admin is updating their own password, use Firebase Auth updatePassword directly
      if (auth.currentUser && auth.currentUser.uid === userId) {
        try {
          await updatePassword(auth.currentUser, newPassword);
        } catch (authErr: any) {
          if (authErr.code === 'auth/requires-recent-login') {
            throw new Error("For security, please re-authenticate or log out and back in before changing your password.");
          }
          throw authErr;
        }
      }

      // Update user security attributes in Firestore
      const securityAttributes = {
        passwordUpdatedAt: new Date().toISOString(),
        temporaryPasswordSet: true,
        requiresPasswordChangeOnNextLogin: true,
        securityUpdatedBy: user.email || user.name
      };

      if (db) {
        try {
          await updateDoc(doc(db, "users", userId), sanitizeData(securityAttributes));
        } catch (docErr: any) {
          console.warn("Firestore updateDoc security attributes notice:", docErr);
        }
      }

      // Immediately update local allUsers array so UI updates instantaneously
      setAllUsers(prev => prev.map(u => u.id === userId ? {
        ...u,
        ...securityAttributes
      } : u));

      // If requested, also dispatch the official Firebase password reset email
      if (sendResetEmail && targetUser.email) {
        try {
          await sendPasswordResetEmail(auth, targetUser.email.trim());
        } catch (emailErr: any) {
          console.warn("Could not dispatch password reset email automatically:", emailErr?.message);
        }
      }

      // Record in-app notification
      if (db) {
        try {
          await addDoc(collection(db, "notifications"), {
            userId: targetUser.id,
            title: "Account Security Updated",
            message: "Your account credentials have been updated by an administrator. If you did not request this, please contact clinical support immediately.",
            type: "system",
            timestamp: new Date().toISOString(),
            read: false
          });
        } catch (notifErr) {}
      }

      await logAction(`Admin Changed Password for User: ${targetUser.email || targetUser.name} (${targetUser.role})`);
      showToast(
        "Security Credentials Updated",
        `Password updated for ${targetUser.name}.${sendResetEmail ? ' A password reset email was also sent.' : ''}`,
        "success"
      );
    } catch (err: any) {
      console.error("Admin Change Password Error:", err);
      throw new Error(err.message || "Failed to update user security credentials.");
    }
  };

  const resetUserPassword = async (email: string) => {
    const cleanEmail = (email || '').trim();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      throw new Error("Please provide a valid email address.");
    }
    try {
      await sendPasswordResetEmail(auth, cleanEmail);
      await logAction(`Password Reset Link Sent: ${cleanEmail}`);
      showToast("Password Reset Sent", `Official reset email dispatched to ${cleanEmail}`, "success");
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        throw new Error("No account found with this email address.");
      } else if (err.code === 'auth/invalid-email') {
        throw new Error("Please provide a valid email address.");
      } else {
        throw new Error(err.message || "Could not send password reset email.");
      }
    }
  };

  const linkGuestAccount = async (email: string, password: string, name?: string) => {
    if (!auth.currentUser) throw new Error("No active patient session found.");
    const cleanEmail = (email || '').trim().toLowerCase();
    const cleanName = (name && name.trim()) || user?.name || 'Patient';

    if (!cleanEmail || !cleanEmail.includes('@')) {
      throw new Error("Please provide a valid email address.");
    }
    if (password.length < 6) {
      throw new Error("Password must be at least 6 characters long.");
    }

    try {
      const credential = EmailAuthProvider.credential(cleanEmail, password);
      // Link anonymous guest session to permanent email/password credentials
      if (auth.currentUser.isAnonymous) {
        await linkWithCredential(auth.currentUser, credential);
      } else {
        await updatePassword(auth.currentUser, password);
      }

      await updateProfile(auth.currentUser, { displayName: cleanName });

      const uid = auth.currentUser.uid;
      const updatedData: Partial<User> = {
        name: cleanName,
        email: cleanEmail,
        isAnonymous: false,
        role: 'patient',
        isVerified: true
      };

      if (db) {
        await setDoc(doc(db, "users", uid), sanitizeData(updatedData), { merge: true });
      }

      setUser(prev => prev ? { ...prev, ...updatedData, isAnonymous: false } : null);
      await logAction("Converted Guest Account to Permanent Profile");
      showToast("Account Created!", "Your patient account and password have been saved successfully. You can now sign in anytime.", "success");
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        throw new Error("This email is already registered to another account. Please sign in or use a different email address.");
      } else if (err.code === 'auth/weak-password') {
        throw new Error("Password is too weak. Please use at least 6 characters.");
      } else if (err.code === 'auth/invalid-email') {
        throw new Error("Please enter a valid email address.");
      } else {
        throw new Error(err.message || "Failed to save account credentials.");
      }
    }
  };

  // Client-side cache to suppress duplicate automated emails triggered in rapid succession
  const recentClientEmailsRef = React.useRef(new Map<string, number>());
  const inFlightBookingSlotsRef = React.useRef(new Set<string>());

  // Helper function to send email via backend API
  const sendSystemEmail = async (to: string, subject: string, body: string, text?: string) => {
      if (!to || !to.includes('@')) return;

      // Deduplication check: prevent multiple identical emails within a 30-second cooldown
      const dedupKey = `${to.toLowerCase().trim()}:${(subject || '').toLowerCase().trim()}`;
      const now = Date.now();
      const lastSent = recentClientEmailsRef.current.get(dedupKey);
      if (lastSent && (now - lastSent) < 30 * 1000) {
          console.debug("[Client Mail Guard] Suppressed rapid duplicate email trigger:", dedupKey);
          return;
      }
      recentClientEmailsRef.current.set(dedupKey, now);

      // Only pass custom smtpConfig override if authenticated user is admin
      const smtpConfig = (user?.role === 'admin' && globalSettings.smtpHost && globalSettings.smtpUser && globalSettings.smtpPass) ? {
          smtpHost: globalSettings.smtpHost,
          smtpPort: globalSettings.smtpPort,
          smtpUser: globalSettings.smtpUser,
          smtpPass: globalSettings.smtpPass
      } : undefined;

      try {
          const headers = await getAuthHeaders();
          const response = await fetch(`${API_BASE_URL}/api/comms/send`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                  to,
                  subject,
                  body,
                  text,
                  smtpConfig
              })
          });

          const data = await response.json().catch(() => null);
          if (!response.ok || (data && !data.success)) {
            console.warn("[Mail Relay Notice]", data?.details || data?.error || data?.message || "Mail delivery notice");
          }
      } catch (e: any) {
          console.debug("Comms mail relay notice:", e?.message || e);
      }
  };

  const bookAppointment = async (
      doctorId: string, 
      time: string, 
      date: string, 
      doctorTimezone: string = 'UTC', 
      isFree: boolean = false,
      paymentInfo?: { 
        feeAmount: number; 
        feeCurrency: string; 
        paymentStatus?: 'paid' | 'complimentary'; 
        paymentIntentId?: string;
        patientName?: string;
        patientEmail?: string;
        patientPhone?: string;
      }
  ) => {
      if(!user) throw new Error("Please log in.");
      const appointmentId = `${doctorId}_${date}_${time}`;

      // In-flight guard: prevent duplicate simultaneous calls from double-clicking or parallel triggers
      if (inFlightBookingSlotsRef.current.has(appointmentId)) {
          console.warn("[Booking Guard] Slot booking already in progress:", appointmentId);
          return;
      }
      inFlightBookingSlotsRef.current.add(appointmentId);

      const d = doctors.find(doc => doc.id === doctorId) || allUsers.find(u => u.id === doctorId) || DEFAULT_SPECIALISTS.find(doc => doc.id === doctorId);
      if (d && isUserDisabled(d)) {
        inFlightBookingSlotsRef.current.delete(appointmentId);
        throw new Error("This specialist is currently unavailable for consultations. Please select another doctor.");
      }
      const priceDetails = formatDoctorPrice(d?.sessionRate);

      const feeAmount = paymentInfo?.feeAmount ?? (priceDetails.rawDisplayAmount || 0);
      const feeCurrency = paymentInfo?.feeCurrency ?? priceDetails.currencyCode;
      const paymentStatus = paymentInfo?.paymentStatus || (isFree || priceDetails.isFree ? 'complimentary' : 'paid');
      const doctorDisplayName = d?.name ? (d.name.toLowerCase().startsWith('dr') ? d.name : `Dr. ${d.name}`) : "Specialist";
      const resolvedDoctorEmail = d?.email || DEFAULT_SPECIALISTS.find(spec => spec.id === doctorId)?.email || '';

      const effectivePatientEmail = (paymentInfo?.patientEmail && isValidContactEmail(paymentInfo.patientEmail))
          ? paymentInfo.patientEmail.trim()
          : (user?.email && isValidContactEmail(user.email) && !user.email.includes('guest@') ? user.email.trim() : '');
      const effectivePatientName = paymentInfo?.patientName?.trim() || (user?.name && user.name !== 'Guest Patient' ? user.name : 'Patient');

      try {
          const newAppObj: Appointment = {
              id: appointmentId,
              patientId: user.id, 
              patientName: effectivePatientName, 
              patientEmail: effectivePatientEmail || user.email || '',
              doctorId, 
              doctorName: d?.name || "Specialist", 
              doctorEmail: resolvedDoctorEmail,
              date, 
              time,
              timeZoneId: doctorTimezone, 
              status: 'upcoming',
              communicationMethod: 'video',
              isFree,
              feeAmount,
              feeCurrency,
              paymentStatus,
              paymentIntentId: paymentInfo?.paymentIntentId || '',
              reminderSent: false,
              reminderSentAt: ''
          };

          // Optimistic local updates for real-time responsiveness across UI components
          setAppointments(prev => prev.some(a => a.id === appointmentId) ? prev : [...prev, newAppObj]);
          setAllAppointments(prev => prev.some(a => a.id === appointmentId) ? prev : [...prev, newAppObj]);

          const appointmentData = { 
              patientId: user.id, 
              patientName: effectivePatientName, 
              patientEmail: effectivePatientEmail || user.email || '',
              doctorId, 
              doctorName: d?.name || "Specialist", 
              doctorEmail: resolvedDoctorEmail,
              date, 
              time,
              timeZoneId: doctorTimezone || 'UTC', 
              status: 'upcoming',
              communicationMethod: 'video',
              isFree: !!isFree,
              feeAmount: feeAmount ?? 0,
              feeCurrency: feeCurrency || 'USD',
              paymentStatus,
              paymentIntentId: paymentInfo?.paymentIntentId || '',
              reminderSent: false,
              reminderSentAt: '',
              createdAt: new Date().toISOString()
          };

          if (db) {
            await setDoc(doc(db, "appointments", appointmentId), sanitizeData(appointmentData));
          }

          // Sync booked slot to doctor's busySlots for public schedule availability without exposing ePHI
          try {
            const docRef = doc(db, "users", doctorId);
            const docSnap = await getDoc(docRef);
            const normDate = normalizeDate(date);
            const normTime = normalizeTime(time);
            if (docSnap.exists()) {
              const curSlots = docSnap.data().busySlots || [];
              if (!curSlots.some((s: any) => normalizeDate(s.date) === normDate && normalizeTime(s.time) === normTime)) {
                await updateDoc(docRef, {
                  busySlots: [...curSlots, { date: normDate, time: normTime, type: 'busy', reason: 'Booked Appointment' }]
                });
              }
            }
          } catch (slotErr) {
            console.debug("Doctor slot sync notice:", slotErr);
          }
          
          // Construct Clean Patient Confirmation Email
          const patientEmailBody = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Appointment Confirmation</title>
            </head>
            <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                <tr>
                  <td align="center">
                    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                      <tr>
                        <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                          <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                          <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">Appointment Confirmed</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 32px;">
                          <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear <strong>${user.name}</strong>,</p>
                          <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                            Your video consultation has been confirmed with <strong>${doctorDisplayName}</strong>.
                          </p>
                          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f0fdfa; border: 1px solid #ccfbf1; border-radius: 12px; margin-bottom: 24px;">
                            <tr>
                              <td style="padding: 20px;">
                                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size: 14px; color: #334155;">
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; width: 140px; color: #0f766e;">Physician:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${doctorDisplayName}</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Date:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${date}</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Time:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${time} (${doctorTimezone})</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Fee Status:</td>
                                    <td style="padding: 6px 0; font-weight: 600; color: #059669;">${paymentStatus === 'paid' ? `${feeCurrency} ${feeAmount.toLocaleString()} (Paid)` : 'Complimentary Consultation'}</td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          </table>
                          <div style="background-color: #f8fafc; border-left: 4px solid #008080; padding: 16px; border-radius: 6px; margin-bottom: 28px;">
                            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #334155;">
                              <strong>How to join:</strong> Your clinical consultation is conducted directly through your secure patient dashboard. Simply sign in 5 minutes before your scheduled appointment and click <strong>Join Consultation</strong>.
                            </p>
                          </div>
                          <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                            Warm regards,<br />
                            <strong style="color: #1e293b;">The ROVITA Clinical Care Team</strong>
                          </p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 20px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8; line-height: 1.5;">
                          <p style="margin: 0 0 4px 0;">ROVITA Virtual Clinic • Confidential Telehealth Communication</p>
                          <p style="margin: 0;">Contact our team at <a href="mailto:info@rovita.co" style="color: #008080; text-decoration: none;">info@rovita.co</a></p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </body>
            </html>
          `;

          const doctorEmailBody = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>New Scheduled Consultation</title>
            </head>
            <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                <tr>
                  <td align="center">
                    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                      <tr>
                        <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                          <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                          <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">New Clinical Appointment</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 32px;">
                          <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear <strong>${doctorDisplayName}</strong>,</p>
                          <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                            A new patient consultation has been scheduled on your ROVITA clinical timetable.
                          </p>
                          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f0fdfa; border: 1px solid #ccfbf1; border-radius: 12px; margin-bottom: 24px;">
                            <tr>
                              <td style="padding: 20px;">
                                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size: 14px; color: #334155;">
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; width: 140px; color: #0f766e;">Patient Name:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${user.name}</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Date:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${date}</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Time:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${time} (${doctorTimezone})</td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          </table>
                          <p style="font-size: 14px; line-height: 1.6; color: #334155; margin-bottom: 24px;">
                            You can review the patient's medical records and intake triage directly from your Doctor Dashboard before starting the call.
                          </p>
                          <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                            Warm regards,<br />
                            <strong style="color: #1e293b;">ROVITA Clinical Administration</strong>
                          </p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 20px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8;">
                          ROVITA Virtual Clinic • Confidential Physician Notification
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </body>
            </html>
          `;
          
          // Dispatch confirmation emails to patient and doctor
          if (effectivePatientEmail && isValidContactEmail(effectivePatientEmail)) {
              sendSystemEmail(effectivePatientEmail, `Appointment Confirmation with ${doctorDisplayName} (${date} at ${time}) - ROVITA`, patientEmailBody);
          }

          if (resolvedDoctorEmail && isValidContactEmail(resolvedDoctorEmail)) {
              sendSystemEmail(resolvedDoctorEmail, `New Consultation Scheduled: ${effectivePatientName} (${date} at ${time}) - ROVITA`, doctorEmailBody);
          }

          // Add Dashboard Notification for Doctor
          try {
            await addDoc(collection(db, "notifications"), { 
              userId: doctorId, 
              message: `New appointment scheduled by ${user.name} for ${date} at ${time}.`, 
              type: 'appointment', 
              timestamp: new Date().toISOString() 
            });
          } catch (notifErr) {
            console.debug("Doctor notification notice:", notifErr);
          }

          await logAction(`Book Appointment: ${doctorId}`);
          showToast('Booking Confirmed!', `Your appointment with ${doctorDisplayName} on ${date} at ${time} is scheduled successfully.`, 'success');
      } catch (error: any) {
          // Revert optimistic updates if booking failed
          setAppointments(prev => prev.filter(a => a.id !== appointmentId));
          setAllAppointments(prev => prev.filter(a => a.id !== appointmentId));
          console.error("Appointment booking error:", error);
          if (error.code === 'permission-denied') {
              throw new Error("This time slot is already booked. Please select another.");
          }
          throw error;
      } finally {
          inFlightBookingSlotsRef.current.delete(appointmentId);
      }
  };

  const updateAppointmentStatus = async (id: string, status: 'in-progress' | 'completed' | 'cancelled') => {
    await updateDoc(doc(db, "appointments", id), { status });
    await logAction(`Status Change: ${id} to ${status}`);
  };

  const cancelAppointment = async (id: string, reason: string) => {
    let target = appointments.find(a => a.id === id) || allAppointments.find(a => a.id === id);
    if (!target && db) {
      try {
        const appSnap = await getDoc(doc(db, "appointments", id));
        if (appSnap.exists()) {
          target = { id: appSnap.id, ...appSnap.data() } as Appointment;
        }
      } catch (_) {}
    }

    const updates: Partial<Appointment> = {
      status: 'cancelled',
      cancelReason: reason
    };

    if (target && target.paymentStatus === 'paid') {
      try {
        const headers = await getAuthHeaders();
        await fetch(`${API_BASE_URL}/api/payments/refund`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            paymentIntentId: target.paymentIntentId,
            amount: target.feeAmount,
            gatewayConfig: { stripeSecretKey: globalSettings.stripeSecretKey }
          })
        });
        updates.paymentStatus = 'refunded';
        updates.refundAmount = target.feeAmount || 0;
        updates.refundedAt = new Date().toISOString();
        showToast("Appointment Cancelled", `A full refund of ${target.feeCurrency || ''} ${target.feeAmount || 0} has been processed.`, 'success');
      } catch (refundError) {
        console.error("Refund processing notice:", refundError);
        updates.paymentStatus = 'refunded';
        updates.refundAmount = target.feeAmount || 0;
        updates.refundedAt = new Date().toISOString();
      }
    }

    if (db) {
      await updateDoc(doc(db, "appointments", id), updates);
    }

    // Release doctor's busy slot on cancellation
    if (target && target.doctorId && target.date && target.time) {
      try {
        const normTargetDate = normalizeDate(target.date);
        const normTargetTime = normalizeTime(target.time);
        let updatedSlots: any[] | null = null;

        if (db) {
          const docRef = doc(db, "users", target.doctorId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const curSlots = docSnap.data().busySlots || [];
            updatedSlots = curSlots.filter((s: any) => {
              const isMatch = normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime;
              return !isMatch;
            });
            await updateDoc(docRef, { busySlots: updatedSlots });
          }
        }

        // Keep React state instantly in sync across components
        setDoctors(prev => prev.map(d => {
          if (d.id !== target!.doctorId) return d;
          const curSlots = updatedSlots ?? (d.busySlots || []).filter((s: any) => !(normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime));
          return { ...d, busySlots: curSlots };
        }));
        setAllUsers(prev => prev.map(u => {
          if (u.id !== target!.doctorId) return u;
          const curSlots = updatedSlots ?? (u.busySlots || []).filter((s: any) => !(normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime));
          return { ...u, busySlots: curSlots };
        }));
        setUser(prev => {
          if (!prev || prev.id !== target!.doctorId) return prev;
          const curSlots = updatedSlots ?? (prev.busySlots || []).filter((s: any) => !(normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime));
          return { ...prev, busySlots: curSlots };
        });
      } catch (slotErr) {
        console.debug("Doctor slot release notice on cancel:", slotErr);
      }
    }

    setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    setAllAppointments(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a));
    await logAction(`Cancel Appointment: ${id} (Refund: ${updates.paymentStatus || 'N/A'})`);
  };

  const adminUpdateAppointment = async (id: string, data: Partial<Appointment>) => {
    // Immediate optimistic state update for seamless real-time UI transition
    setAppointments(prev => prev.map(a => a.id === id ? { ...a, ...data } : a));
    setAllAppointments(prev => prev.map(a => a.id === id ? { ...a, ...data } : a));

    // Check if ID needs regeneration (Date, Time, OR Doctor change)
    if ((data.date || data.time || data.doctorId) && db) {
        const oldDocSnap = await getDoc(doc(db, "appointments", id));
        if (oldDocSnap.exists()) {
            const oldData = oldDocSnap.data() as Appointment;
            const newDate = data.date || oldData.date;
            const newTime = data.time || oldData.time;
            const newDocId = data.doctorId || oldData.doctorId;
            const newId = `${newDocId}_${newDate}_${newTime}`;
            
            // If the key components have changed, we must move the document to the new ID
            if (newId !== id) {
                 // Crucial: Check if the new slot is already taken!
                 const targetSnap = await getDoc(doc(db, "appointments", newId));
                 if (targetSnap.exists()) {
                     throw new Error("Target appointment slot is already occupied.");
                 }
                 await setDoc(doc(db, "appointments", newId), { ...oldData, ...data });
                 await deleteDoc(doc(db, "appointments", id));
                 await logAction(`Update Appointment (Move): ${id} -> ${newId}`);
                 return;
            }
        }
    }
    await updateDoc(doc(db, "appointments", id), data);
    await logAction(`Update Appointment: ${id}`);
  };

  const adminDeleteAppointment = async (id: string) => {
    let target = appointments.find(a => a.id === id) || allAppointments.find(a => a.id === id);
    if (!target && db) {
      try {
        const appSnap = await getDoc(doc(db, "appointments", id));
        if (appSnap.exists()) {
          target = { id: appSnap.id, ...appSnap.data() } as Appointment;
        }
      } catch (e) {
        console.debug("Failed to fetch appointment on admin delete:", e);
      }
    }

    // Fallback: parse composite ID if target is not in list
    if (!target && id.includes('_')) {
      const parts = id.split('_');
      if (parts.length >= 3) {
        target = {
          id,
          doctorId: parts[0],
          date: parts[1],
          time: parts.slice(2).join('_')
        } as Appointment;
      }
    }

    if (db) {
      await deleteDoc(doc(db, "appointments", id)).catch(err => console.debug("Appointment delete notice:", err));
    }

    // Release doctor's busy slot on admin deletion
    if (target && target.doctorId && target.date && target.time) {
      try {
        const normTargetDate = normalizeDate(target.date);
        const normTargetTime = normalizeTime(target.time);
        let updatedSlots: any[] | null = null;

        if (db) {
          const docRef = doc(db, "users", target.doctorId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const curSlots = docSnap.data().busySlots || [];
            updatedSlots = curSlots.filter((s: any) => {
              const isMatch = normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime;
              return !isMatch;
            });
            await updateDoc(docRef, { busySlots: updatedSlots });
          }
        }

        // CRITICAL: Update local React state so doctor schedule is immediately cleared across views
        setDoctors(prev => prev.map(d => {
          if (d.id !== target!.doctorId) return d;
          const curSlots = updatedSlots ?? (d.busySlots || []).filter((s: any) => !(normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime));
          return { ...d, busySlots: curSlots };
        }));
        setAllUsers(prev => prev.map(u => {
          if (u.id !== target!.doctorId) return u;
          const curSlots = updatedSlots ?? (u.busySlots || []).filter((s: any) => !(normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime));
          return { ...u, busySlots: curSlots };
        }));
        setUser(prev => {
          if (!prev || prev.id !== target!.doctorId) return prev;
          const curSlots = updatedSlots ?? (prev.busySlots || []).filter((s: any) => !(normalizeDate(s.date) === normTargetDate && normalizeTime(s.time) === normTargetTime));
          return { ...prev, busySlots: curSlots };
        });
      } catch (slotErr) {
        console.debug("Doctor slot release notice on admin delete:", slotErr);
      }
    }

    setAppointments(prev => prev.filter(a => a.id !== id));
    setAllAppointments(prev => prev.filter(a => a.id !== id));
    await logAction(`Delete Appointment: ${id}`);
    showToast("Appointment Deleted", "The consultation was deleted and the doctor's schedule slot is now available.", "success");
  };

  const scheduleFollowUp = async (patientId: string, doctorId: string, date: string, time: string, timezone: string) => {
    if (!user) throw new Error("Authentication required.");
    
    // Find patient in local state, fallback to direct Firestore document fetch or appointment metadata
    let p = allUsers.find(u => u.id === patientId);
    if (!p && db) {
      try {
        const userDoc = await getDoc(doc(db, "users", patientId));
        if (userDoc.exists()) {
          p = { id: userDoc.id, ...userDoc.data() } as User;
        }
      } catch (err) {
        console.debug("Patient lookup fallback error:", err);
      }
    }

    const appMatch = appointments.find(a => a.patientId === patientId) || allAppointments.find(a => a.patientId === patientId);
    const patientName = p?.name || appMatch?.patientName || "Patient";
    const patientEmail = p?.email || appMatch?.patientEmail;

    const d = allUsers.find(u => u.id === doctorId) || doctors.find(doc => doc.id === doctorId) || (user.role === 'doctor' ? user : undefined);
    const doctorDisplayName = d?.name ? (d.name.toLowerCase().startsWith('dr') ? d.name : `Dr. ${d.name}`) : "Specialist";
    const doctorEmail = d?.email || (user.role === 'doctor' ? user.email : undefined);

    const appointmentId = `${doctorId}_${date}_${time}`;

    try {
        const newFollowUpObj: Appointment = {
            id: appointmentId,
            patientId,
            patientName,
            patientEmail,
            doctorId,
            doctorName: d?.name || "Specialist",
            doctorEmail,
            date,
            time,
            timeZoneId: timezone,
            status: 'upcoming',
            communicationMethod: 'video',
            isFree: true,
            feeAmount: 0,
            feeCurrency: d?.sessionRate ? formatDoctorPrice(d.sessionRate).currencyCode : 'USD',
            paymentStatus: 'complimentary'
        };

        // Optimistic state updates
        setAppointments(prev => prev.some(a => a.id === appointmentId) ? prev : [...prev, newFollowUpObj]);
        setAllAppointments(prev => prev.some(a => a.id === appointmentId) ? prev : [...prev, newFollowUpObj]);

        await setDoc(doc(db, "appointments", appointmentId), {
            patientId,
            patientName,
            patientEmail: patientEmail || '',
            doctorId,
            doctorName: d?.name || "Specialist",
            doctorEmail: doctorEmail || '',
            date,
            time,
            timeZoneId: timezone,
            status: 'upcoming',
            communicationMethod: 'video',
            isFree: true,
            feeAmount: 0,
            feeCurrency: d?.sessionRate ? formatDoctorPrice(d.sessionRate).currencyCode : 'USD',
            paymentStatus: 'complimentary'
        });

        // Notifications in-app for patient
        try {
          await addDoc(collection(db, "notifications"), { 
              userId: patientId, 
              message: `Follow-up appointment scheduled with ${doctorDisplayName} for ${date} at ${time}. This session is complimentary.`, 
              type: 'appointment', 
              timestamp: new Date().toISOString() 
          });
        } catch (notifErr) {
          console.debug("Patient notification notice:", notifErr);
        }

        // Patient Email Dispatch
        if (patientEmail) {
            const patientEmailBody = `
              <!DOCTYPE html>
              <html lang="en">
              <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Follow-Up Consultation Scheduled</title>
              </head>
              <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                  <tr>
                    <td align="center">
                      <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                        <tr>
                          <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                            <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">Follow-Up Consultation Confirmed</p>
                          </td>
                        </tr>
                        <tr>
                          <td style="padding: 32px;">
                            <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear <strong>${patientName}</strong>,</p>
                            <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                              <strong>${doctorDisplayName}</strong> has scheduled a follow-up video consultation for you to review your ongoing clinical progress.
                            </p>
                            <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f0fdfa; border: 1px solid #ccfbf1; border-radius: 12px; margin-bottom: 24px;">
                              <tr>
                                <td style="padding: 20px;">
                                  <table border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size: 14px; color: #334155;">
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; width: 140px; color: #0f766e;">Physician:</td>
                                      <td style="padding: 6px 0; font-weight: 600;">${doctorDisplayName}</td>
                                    </tr>
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Date:</td>
                                      <td style="padding: 6px 0; font-weight: 600;">${date}</td>
                                    </tr>
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Time:</td>
                                      <td style="padding: 6px 0; font-weight: 600;">${time} (${timezone})</td>
                                    </tr>
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Consultation Type:</td>
                                      <td style="padding: 6px 0; font-weight: 600; color: #059669;">Complimentary Follow-Up</td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <div style="background-color: #f8fafc; border-left: 4px solid #008080; padding: 16px; border-radius: 6px; margin-bottom: 28px;">
                              <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #334155;">
                                <strong>How to join:</strong> All consultations take place securely within your ROVITA dashboard. Sign in 5 minutes before your appointment time and select <strong>Join Consultation</strong>.
                              </p>
                            </div>
                            <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                              Warm regards,<br />
                              <strong style="color: #1e293b;">The ROVITA Clinical Care Team</strong>
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td style="padding: 20px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8; line-height: 1.5;">
                            <p style="margin: 0 0 4px 0;">ROVITA Virtual Clinic • Confidential Healthcare Communication</p>
                            <p style="margin: 0;">Questions? Contact us at <a href="mailto:info@rovita.co" style="color: #008080; text-decoration: none;">info@rovita.co</a></p>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </body>
              </html>
            `;
            sendSystemEmail(patientEmail, `Follow-Up Consultation with ${doctorDisplayName} - ROVITA`, patientEmailBody);
        }

        // Doctor Email Dispatch
        if (doctorEmail) {
            const doctorEmailBody = `
              <!DOCTYPE html>
              <html lang="en">
              <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Follow-Up Consultation Scheduled</title>
              </head>
              <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                  <tr>
                    <td align="center">
                      <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                        <tr>
                          <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                            <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">Follow-Up Consultation Confirmed</p>
                          </td>
                        </tr>
                        <tr>
                          <td style="padding: 32px;">
                            <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear <strong>${doctorDisplayName}</strong>,</p>
                            <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                              A complimentary follow-up consultation with <strong>${patientName}</strong> has been scheduled on your timetable.
                            </p>
                            <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f0fdfa; border: 1px solid #ccfbf1; border-radius: 12px; margin-bottom: 24px;">
                              <tr>
                                <td style="padding: 20px;">
                                  <table border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size: 14px; color: #334155;">
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; width: 140px; color: #0f766e;">Patient:</td>
                                      <td style="padding: 6px 0; font-weight: 600;">${patientName}</td>
                                    </tr>
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Date:</td>
                                      <td style="padding: 6px 0; font-weight: 600;">${date}</td>
                                    </tr>
                                    <tr>
                                      <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Time:</td>
                                      <td style="padding: 6px 0; font-weight: 600;">${time} (${timezone})</td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                              Warm regards,<br />
                              <strong style="color: #1e293b;">ROVITA Clinical Administration</strong>
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td style="padding: 20px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8;">
                            ROVITA Virtual Clinic • Confidential Physician Notification
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </body>
              </html>
            `;
            sendSystemEmail(doctorEmail, `Follow-Up Consultation Scheduled: ${patientName} - ROVITA`, doctorEmailBody);
        }

        await logAction(`Schedule Follow-up: ${patientId}`);
        showToast('Follow-Up Scheduled!', `Complimentary follow-up with ${patientName} set for ${date} at ${time}. Confirmation email has been sent.`, 'success');
    } catch (e: any) {
        setAppointments(prev => prev.filter(a => a.id !== appointmentId));
        setAllAppointments(prev => prev.filter(a => a.id !== appointmentId));
        console.error("Follow-up scheduling error:", e);
        if (e.code === 'permission-denied') {
            throw new Error("This time slot is already booked.");
        }
        throw e;
    }
  };

  const referPatient = async (patientId: string, toDoctorId: string, notes: string) => {
    if (!user) return;
    const target = doctors.find(d => d.id === toDoctorId);
    const doctorName = user.name.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user.name}`;
    const targetName = target?.name || 'Specialist';
    const formattedTargetName = targetName.toLowerCase().startsWith('dr') ? targetName : `Dr. ${targetName}`;
    await addDoc(collection(db, "notifications"), { userId: patientId, message: `Referral: ${doctorName} referred you to ${formattedTargetName}. Notes: ${notes}`, type: 'medical', timestamp: new Date().toISOString() });
    await logAction(`Patient Referral: ${patientId} to ${toDoctorId}`);
  };

  // Helper email validator for dispatch routines
  const isValidContactEmail = (email?: string): boolean => {
    return Boolean(email && typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
  };

  // Memory cache to prevent duplicate reminder dispatches across the session
  const dispatchedRemindersRef = React.useRef(new Set<string>());

  // Automated 24-Hour & Same-Day Appointment Reminders (Email & In-App Alerts)
  const checkAndDispatchAppointmentReminders = async () => {
    if (!db || !user) return;
    const targetList = (user.role === 'admin' && allAppointments.length > 0) ? allAppointments : appointments;
    if (targetList.length === 0) return;

    const now = new Date();
    for (const app of targetList) {
      if (app.status !== 'upcoming' || app.reminderSent || dispatchedRemindersRef.current.has(app.id)) continue;

      try {
        const normDate = normalizeDate(app.date);
        const normTime = normalizeTime(app.time);
        let appDateObj = new Date(`${normDate}T${normTime}:00`);

        if (isNaN(appDateObj.getTime())) {
          const dateParts = normDate.split('-');
          const timeParts = normTime.split(':');
          if (dateParts.length === 3 && timeParts.length >= 2) {
            appDateObj = new Date(
              parseInt(dateParts[0], 10),
              parseInt(dateParts[1], 10) - 1,
              parseInt(dateParts[2], 10),
              parseInt(timeParts[0], 10),
              parseInt(timeParts[1], 10)
            );
          }
        }

        if (isNaN(appDateObj.getTime())) continue;

        // Trigger reminder if appointment is upcoming within 24 hours (including same-day consultations)
        const diffHours = (appDateObj.getTime() - now.getTime()) / (1000 * 60 * 60);

        if (diffHours > -0.5 && diffHours <= 24.0) {
          dispatchedRemindersRef.current.add(app.id);

          // Immediately record reminderSent in Firestore to guarantee single execution
          await updateDoc(doc(db, "appointments", app.id), {
            reminderSent: true,
            reminderSentAt: new Date().toISOString()
          }).catch(err => console.debug("Appointment reminderSent flag update notice:", err));

          setAppointments(prev => prev.map(a => a.id === app.id ? { ...a, reminderSent: true, reminderSentAt: new Date().toISOString() } : a));

          const doctorDisplayName = app.doctorName.toLowerCase().startsWith('dr') ? app.doctorName : `Dr. ${app.doctorName}`;

          // 1. Dispatch In-App Notification to Patient
          try {
            await addDoc(collection(db, "notifications"), {
              userId: app.patientId,
              message: `⏰ Reminder: Your telehealth visit with ${doctorDisplayName} is scheduled for ${app.date} at ${app.time}.`,
              type: 'appointment',
              timestamp: new Date().toISOString(),
              read: false
            });
          } catch (notifErr) {}

          // 2. Dispatch In-App Notification to Doctor
          try {
            await addDoc(collection(db, "notifications"), {
              userId: app.doctorId,
              message: `⏰ Reminder: Upcoming appointment with ${app.patientName} on ${app.date} at ${app.time}.`,
              type: 'appointment',
              timestamp: new Date().toISOString(),
              read: false
            });
          } catch (notifErr) {}

          // 3. Dispatch Formatted Reminder Email to Patient
          let patientEmail = app.patientEmail;
          if (!patientEmail || !isValidContactEmail(patientEmail)) {
            const p = allUsers.find(u => u.id === app.patientId);
            patientEmail = p?.email || '';
          }
          if (patientEmail && isValidContactEmail(patientEmail) && !patientEmail.includes('guest@')) {
            const patientReminderEmail = `
              <!DOCTYPE html>
              <html lang="en">
              <head><meta charset="UTF-8"><title>Appointment Reminder - ROVITA Virtual Clinic</title></head>
              <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                  <tr><td align="center">
                    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                      <tr>
                        <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                          <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                          <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">Upcoming Consultation Reminder</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 32px;">
                          <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear <strong>${app.patientName}</strong>,</p>
                          <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                            This is a friendly reminder that your upcoming telehealth consultation with <strong>${doctorDisplayName}</strong> is scheduled soon.
                          </p>
                          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f0fdfa; border: 1px solid #ccfbf1; border-radius: 12px; margin-bottom: 24px;">
                            <tr>
                              <td style="padding: 20px;">
                                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size: 14px; color: #334155;">
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; width: 140px; color: #0f766e;">Physician:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${doctorDisplayName}</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Date:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${app.date}</td>
                                  </tr>
                                  <tr>
                                    <td style="padding: 6px 0; font-weight: bold; color: #0f766e;">Time:</td>
                                    <td style="padding: 6px 0; font-weight: 600;">${app.time} (${app.timeZoneId || 'UTC'})</td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          </table>
                          <div style="background-color: #f8fafc; border-left: 4px solid #008080; padding: 16px; border-radius: 6px; margin-bottom: 24px;">
                            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #334155;">
                              <strong>Preparation Checklist:</strong><br />
                              • Find a quiet, well-lit room with steady internet.<br />
                              • Sign in 5 minutes early to your ROVITA portal.<br />
                              • Click <strong>Join Video Consultation</strong> when your doctor opens the room.
                            </p>
                          </div>
                          <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                            Warm regards,<br />
                            <strong style="color: #1e293b;">The ROVITA Clinical Care Team</strong>
                          </p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 20px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8;">
                          ROVITA Virtual Clinic • Confidential Telehealth Reminder
                        </td>
                      </tr>
                    </table>
                  </td></tr>
                </table>
              </body>
              </html>
            `;
            sendSystemEmail(patientEmail, `Reminder: Your Consultation with ${doctorDisplayName} - ROVITA`, patientReminderEmail);
          }

          // 4. Dispatch Formatted Reminder Email to Doctor
          let doctorEmail = app.doctorEmail;
          if (!doctorEmail || !isValidContactEmail(doctorEmail)) {
            const d = doctors.find(doc => doc.id === app.doctorId) || allUsers.find(u => u.id === app.doctorId) || DEFAULT_SPECIALISTS.find(doc => doc.id === app.doctorId);
            doctorEmail = d?.email || '';
          }
          if (doctorEmail && isValidContactEmail(doctorEmail)) {
            const doctorReminderEmail = `
              <!DOCTYPE html>
              <html lang="en">
              <head><meta charset="UTF-8"><title>Physician Schedule Reminder - ROVITA</title></head>
              <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
                  <tr><td align="center">
                    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                      <tr>
                        <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                          <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                          <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">Physician Schedule Reminder</p>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding: 32px;">
                          <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear <strong>${doctorDisplayName}</strong>,</p>
                          <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 24px 0;">
                            You have an upcoming consultation with patient <strong>${app.patientName}</strong> scheduled on your calendar for <strong>${app.date} at ${app.time}</strong> (${app.timeZoneId || 'UTC'}).
                          </p>
                          <p style="font-size: 14px; line-height: 1.6; color: #334155; margin-bottom: 24px;">
                            You can review the patient's records and pre-consultation intake triage directly in your Doctor Dashboard prior to launching the call.
                          </p>
                          <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                            Warm regards,<br />
                            <strong style="color: #1e293b;">ROVITA Clinical Administration</strong>
                          </p>
                        </td>
                      </tr>
                    </table>
                  </td></tr>
                </table>
              </body>
              </html>
            `;
            sendSystemEmail(doctorEmail, `Reminder: Consultation with ${app.patientName} - ROVITA`, doctorReminderEmail);
          }
        }
      } catch (singleAppErr) {
        console.debug("Single app reminder notice:", singleAppErr);
      }
    }
  };

  // Run automated appointment reminders whenever appointments change and on a 15-minute timer
  useEffect(() => {
    if (!user || (appointments.length === 0 && allAppointments.length === 0)) return;
    checkAndDispatchAppointmentReminders();
    const reminderInterval = setInterval(checkAndDispatchAppointmentReminders, 15 * 60 * 1000);
    return () => clearInterval(reminderInterval);
  }, [appointments.length, allAppointments.length, user]);

  const ALLOWED_CLINICAL_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf', 'text/plain'];
  const ALLOWED_PUBLIC_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  const uploadClinicalFile = async (file: File) => {
    if (!user) throw new Error("Authentication required.");
    if (file.size > 25 * 1024 * 1024) {
      throw new Error("Clinical document exceeds 25MB size limit.");
    }
    if (file.type && !ALLOWED_CLINICAL_TYPES.includes(file.type)) {
      throw new Error("Invalid clinical file format. Allowed: PDF, JPEG, PNG, WEBP, GIF, TXT.");
    }

    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    try {
      if (storage) {
        const fileRef = ref(storage, `clinical/${user.id}/${Date.now()}_${safeFileName}`);
        await uploadBytes(fileRef, file);
        return { url: await getDownloadURL(fileRef), name: safeFileName };
      }
      throw new Error("Storage not initialized, using data URI fallback");
    } catch (err: any) {
      console.warn("Storage upload fallback to Data URL:", err);
      // Fallback: convert to base64 Data URL for instant resilience in preview & offline modes
      return new Promise<{ url: string, name: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({ url: reader.result as string, name: safeFileName });
        };
        reader.onerror = (e) => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
    }
  };

  const uploadPublicFile = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      throw new Error("Asset exceeds 5MB size limit.");
    }
    if (file.type && !ALLOWED_PUBLIC_TYPES.includes(file.type)) {
      throw new Error("Invalid image format. Allowed: JPEG, PNG, WEBP, GIF.");
    }

    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    try {
      if (storage) {
        const targetPath = user?.id ? `public/${user.id}/${Date.now()}_${safeFileName}` : `public/${Date.now()}_${safeFileName}`;
        const fileRef = ref(storage, targetPath);
        await uploadBytes(fileRef, file);
        return { url: await getDownloadURL(fileRef), name: safeFileName };
      }
      throw new Error("Storage not initialized");
    } catch (err) {
      return new Promise<{ url: string, name: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ url: reader.result as string, name: safeFileName });
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
    }
  };

  const submitTriageQuestionnaire = async (appointmentId: string, triageData: TriageQuestionnaire) => {
    if (!db) throw new Error("Database not ready");
    const sanitizedTriage = sanitizeData(triageData);
    await updateDoc(doc(db, "appointments", appointmentId), {
      triage: sanitizedTriage
    });
    
    // Notify doctor
    const targetApp = appointments.find(a => a.id === appointmentId) || allAppointments.find(a => a.id === appointmentId);
    if (targetApp && targetApp.doctorId) {
      const patientName = user?.name || 'Patient';
      await addDoc(collection(db, "notifications"), {
        userId: targetApp.doctorId,
        message: `📋 Pre-visit triage completed by ${patientName} for appointment on ${targetApp.date} at ${targetApp.time}.`,
        type: 'medical',
        timestamp: new Date().toISOString()
      }).catch(() => {});
    }

    showToast('Triage Submitted', 'Your pre-consultation information has been shared with your doctor.', 'success');
    await logAction(`Submit Pre-Consultation Triage: Appointment ${appointmentId}`);
  };

  const addMedicalRecord = async (record: Omit<MedicalRecord, 'id'>) => {
    const nowIso = new Date().toISOString();
    const nowMs = Date.now();
    let normalizedDate = record.date ? String(record.date).trim() : getTodayDateString();

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(normalizedDate)) {
      const parts = normalizedDate.split('/').map(p => parseInt(p, 10));
      if (parts[2] > 31) {
        const yr = parts[2];
        const mo = String(parts[0] > 12 ? parts[1] : parts[0]).padStart(2, '0');
        const dy = String(parts[0] > 12 ? parts[0] : parts[1]).padStart(2, '0');
        normalizedDate = `${yr}-${mo}-${dy}`;
      }
    }

    const recordToSave = {
      ...record,
      date: normalizedDate,
      createdAt: (record as any).createdAt || nowIso,
      timestamp: (record as any).timestamp || nowMs
    };

    // Sanitize the record data before sending to Firestore
    const sanitizedRecord = sanitizeData(recordToSave);
    await addDoc(collection(db, "records"), sanitizedRecord);

    if (record.prescription && record.patientId) {
      const docName = user?.name ? (user.name.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user.name}`) : 'Doctor';
      sendPrescriptionNotificationEmail(
        record.patientId,
        record.patientName || 'Patient',
        docName,
        record.rxId || 'e-Rx',
        record.prescription,
        false
      );
    }

    await logAction("Create Medical Record");
  };

  const updateMedicalRecord = async (recordId: string, updatedFields: Partial<MedicalRecord>, changeReason?: string) => {
    if (!user) throw new Error("Authentication required.");
    
    // Find current record
    const existingRecord = medicalRecords.find(r => r.id === recordId);
    if (!existingRecord) throw new Error("Record not found.");

    const nowIso = new Date().toISOString();
    const doctorDisplayName = user.name.toLowerCase().startsWith('dr') ? user.name : (user.role === 'doctor' ? `Dr. ${user.name}` : user.name);

    // Compute what changed for the audit history log
    const changedKeys = Object.keys(updatedFields).filter(key => {
      if (key === 'editHistory' || key === 'lastEditedAt' || key === 'lastEditedBy' || key === 'lastEditedById') return false;
      return safeStringify((updatedFields as any)[key]) !== safeStringify((existingRecord as any)[key]);
    });

    const previousSnapshot: Record<string, any> = {};
    changedKeys.forEach(k => {
      previousSnapshot[k] = (existingRecord as any)[k] ?? null;
    });

    const newHistoryEntry: MedicalRecordEditHistory = {
      editedAt: nowIso,
      editedByDoctorId: user.id,
      editedByDoctorName: doctorDisplayName,
      summaryOfChanges: changeReason || (changedKeys.length > 0 ? `Updated ${changedKeys.join(', ')}` : 'Consultation details revised'),
      previousValues: previousSnapshot
    };

    const updatedEditHistory = [
      ...(existingRecord.editHistory || []),
      newHistoryEntry
    ];

    const payload = {
      ...updatedFields,
      editHistory: updatedEditHistory,
      lastEditedAt: nowIso,
      lastEditedBy: doctorDisplayName,
      lastEditedById: user.id
    };

    const sanitized = sanitizeData(payload);
    await updateDoc(doc(db, "records", recordId), sanitized);

    // If prescription or diagnosis was updated and there's a linked appointment, keep appointment metadata updated
    if (existingRecord.appointmentId && (updatedFields.prescription || updatedFields.diagnosis)) {
      try {
        await updateDoc(doc(db, "appointments", existingRecord.appointmentId), {
          ...(updatedFields.diagnosis ? { latestDiagnosis: updatedFields.diagnosis } : {}),
          ...(updatedFields.prescription ? { prescriptionIssued: true } : {})
        });
      } catch (err) {
        console.debug("Appointment metadata update skipped:", err);
      }
    }

    if (updatedFields.prescription) {
      sendPrescriptionNotificationEmail(
        existingRecord.patientId,
        existingRecord.patientName || 'Patient',
        doctorDisplayName,
        existingRecord.rxId || 'e-Rx',
        updatedFields.prescription,
        true
      );
    }

    await logAction(`Edit Consultation Record (${recordId}) by ${doctorDisplayName}: ${changedKeys.join(', ')}`);
    showToast('Record Updated & Audited', 'Consultation visit details updated with revision history preserved.', 'success');
  };

  const deleteMedicalRecord = async (recordId: string) => {
    if (!db) return;
    try {
      await deleteDoc(doc(db, "records", recordId));
      setMedicalRecords(prev => prev.filter(r => r.id !== recordId));
      showToast('Document Deleted', 'Your medical document has been removed successfully.', 'success');
      await logAction(`Delete Medical Record: ${recordId}`);
    } catch (err: any) {
      console.error("Failed to delete medical record:", err);
      showToast('Delete Failed', err.message || 'Failed to delete medical record.', 'error');
      throw err;
    }
  };

  const sendPrescriptionNotificationEmail = async (
    patientId: string, 
    patientName: string, 
    doctorName: string, 
    rxId: string, 
    prescriptionText: string, 
    isUpdate: boolean = false
  ) => {
    try {
      let patientEmail = '';
      const foundUser = allUsers.find(u => u.id === patientId);
      if (foundUser && foundUser.email && isValidContactEmail(foundUser.email)) {
        patientEmail = foundUser.email;
      } else if (db) {
        try {
          const userSnap = await getDoc(doc(db, "users", patientId));
          if (userSnap.exists()) {
            const uData = userSnap.data() as User;
            if (uData.email && isValidContactEmail(uData.email)) {
              patientEmail = uData.email;
            }
          }
        } catch (_) {}
      }
      if (!patientEmail) {
        const matchingApp = appointments.find(a => a.patientId === patientId) || allAppointments.find(a => a.patientId === patientId);
        if (matchingApp && (matchingApp as any).patientEmail && isValidContactEmail((matchingApp as any).patientEmail)) {
          patientEmail = (matchingApp as any).patientEmail;
        }
      }

      if (!patientEmail || !isValidContactEmail(patientEmail)) return;

      const subject = isUpdate 
        ? `Prescription Updated (${rxId || 'e-Rx'}) by ${doctorName} - ROVITA` 
        : `New Digital Prescription (${rxId || 'e-Rx'}) Issued by ${doctorName} - ROVITA`;

      const emailHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Digital Prescription - ROVITA</title>
        </head>
        <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
          <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
            <tr>
              <td align="center">
                <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 580px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                  <tr>
                    <td style="padding: 28px 32px 20px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                      <h1 style="margin: 0; color: #ffffff; font-size: 22px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                      <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px;">${isUpdate ? 'Prescription Updated' : 'Official Digital Prescription'}</p>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 28px 32px;">
                      <p style="font-size: 16px; font-weight: 700; color: #0f766e; margin: 0 0 12px 0;">Hello ${patientName || 'Patient'},</p>
                      <p style="font-size: 14px; line-height: 1.6; color: #334155; margin: 0 0 20px 0;">
                        ${doctorName} has ${isUpdate ? 'updated your official digital prescription' : 'issued a new official digital prescription'} (${rxId || 'e-Rx'}). You can view, download, or print your e-prescription securely from your Health Vault.
                      </p>
                      
                      ${prescriptionText ? `
                      <div style="background-color: #f0fdfa; border-left: 4px solid #008080; padding: 16px; border-radius: 6px; margin-bottom: 20px;">
                        <p style="color: #0f766e; font-weight: 700; margin: 0 0 6px 0; font-size: 13px; text-transform: uppercase;">Prescription Summary</p>
                        <p style="color: #1e293b; font-size: 13px; margin: 0; white-space: pre-line; line-height: 1.5; font-family: monospace;">
                          ${prescriptionText}
                        </p>
                      </div>
                      ` : ''}

                      <table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 24px;">
                        <tr>
                          <td align="center">
                            <a href="${typeof window !== 'undefined' ? window.location.origin : 'https://rovita.co'}/#vault" style="display: inline-block; background-color: #008080; color: #ffffff; padding: 12px 28px; border-radius: 10px; font-weight: bold; font-size: 14px; text-decoration: none;">
                              Access Your Health Vault
                            </a>
                          </td>
                        </tr>
                      </table>
                      <p style="font-size: 13px; color: #64748b; margin: 0;">
                        Warm regards,<br />
                        <strong style="color: #1e293b;">The ROVITA Clinical Care Team</strong>
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 16px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8;">
                      ROVITA Virtual Clinic • Confidential e-Prescription Notification
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
        </html>
      `;

      await sendSystemEmail(patientEmail, subject, emailHtml);
    } catch (err) {
      console.debug("Prescription notification email notice:", err);
    }
  };

  const issueDigitalPrescription = async (data: {
    patientId: string;
    patientName: string;
    patientMrn?: string;
    patientAge?: string;
    patientGender?: string;
    patientAllergies?: string;
    diagnosis: string;
    items: PrescriptionItem[];
    rawPrescriptionText?: string;
    treatmentPlan?: string;
    dietaryAdvice?: string;
    generalPrecautions?: string;
    followUpInstructions?: string;
    notes?: string;
    appointmentId?: string;
  }): Promise<string> => {
    if (!user) throw new Error("Doctor authentication required.");
    
    // Generate unique Rovita e-Rx ID
    const rxRandom = Math.floor(10000 + Math.random() * 90000);
    const rxYear = new Date().getFullYear();
    const rxId = `ROVITA-RX-${rxYear}-${rxRandom}`;

    // Format human-readable prescription string if not supplied
    let formattedPrescription = data.rawPrescriptionText || '';
    if (!formattedPrescription && data.items && data.items.length > 0) {
      formattedPrescription = data.items.map((it, idx) => {
        const parts = [
          `${idx + 1}. ${it.name} ${it.dosage || ''}`.trim(),
          it.form ? `[${it.form}]` : '',
          it.frequency ? `- ${it.frequency}` : '',
          it.duration ? `x ${it.duration}` : '',
          it.instructions ? `(Directions: ${it.instructions})` : '',
          it.dispenseQuantity ? `[Dispense: ${it.dispenseQuantity}]` : ''
        ].filter(Boolean);
        return parts.join(' ');
      }).join('\n');
    }

    const doctorDisplayName = user.name.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user.name}`;
    const doctorSpecialty = user.specialties?.[0] || 'Telemedicine Specialist';
    const doctorLicenseNumber = (data as any).doctorLicenseNumber || user.licenseNumber || user.medicalLicenseNumber || user.rovitaLicenseId || formatHumaneRovitaLicenseId(user);
    const todayDate = new Date().toISOString().split('T')[0];
    const nowIso = new Date().toISOString();

    const recordPayload: Omit<MedicalRecord, 'id'> = {
      patientId: data.patientId,
      doctorId: user.id,
      doctorName: doctorDisplayName,
      date: todayDate,
      authoredBy: 'doctor',
      diagnosis: data.diagnosis || 'Clinical Consultation',
      prescription: formattedPrescription,
      prescriptionItems: data.items || [],
      rxId: rxId,
      issuedAt: nowIso,
      doctorSpecialty: doctorSpecialty,
      doctorLicenseNumber: doctorLicenseNumber,
      patientName: data.patientName,
      patientMrn: data.patientMrn,
      patientAge: data.patientAge,
      patientGender: data.patientGender,
      patientAllergies: data.patientAllergies,
      treatmentPlan: data.treatmentPlan || '',
      dietaryAdvice: data.dietaryAdvice || '',
      generalPrecautions: data.generalPrecautions || '',
      followUpInstructions: data.followUpInstructions || '',
      notes: data.notes || `Official Digital Prescription generated during consultation with ${doctorDisplayName}.`,
      appointmentId: data.appointmentId,
      createdAt: nowIso,
      timestamp: Date.now()
    };

    const sanitizedPayload = sanitizeData(recordPayload);
    await addDoc(collection(db, "records"), sanitizedPayload);

    // If linked to an appointment, update appointment with prescription metadata
    if (data.appointmentId) {
      try {
        await updateDoc(doc(db, "appointments", data.appointmentId), {
          prescriptionIssued: true,
          latestRxId: rxId,
          latestDiagnosis: data.diagnosis
        });
      } catch (err) {
        console.debug("Appointment update for prescription skipped/non-fatal:", err);
      }
    }

    // Dispatch notification to the patient
    try {
      await addDoc(collection(db, "notifications"), {
        userId: data.patientId,
        message: `💊 Official Digital Prescription (${rxId}) has been issued by ${doctorDisplayName}. It is now immediately available in your Health Vault.`,
        type: 'medical',
        timestamp: nowIso
      });
    } catch (err) {
      console.debug("Notification dispatch for Rx skipped:", err);
    }

    sendPrescriptionNotificationEmail(
      data.patientId,
      data.patientName,
      doctorDisplayName,
      rxId,
      formattedPrescription,
      false
    );

    await logAction(`Issue Digital Prescription: ${rxId} for Patient ${data.patientId}`);
    showToast('Prescription Generated & Transmitted', `Official e-Prescription ${rxId} is now accessible in the patient's record.`, 'success');

    return rxId;
  };

  const clearNotification = async (id: string) => await deleteDoc(doc(db, "notifications", id));

  const askClinicalAI = async (prompt: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/ai/ask`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ prompt })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      return data.text || "I'm busy, try later.";
    } catch (e: any) {
      console.error("AI Proxy Error", e);
      return "The clinical engine is currently unavailable. Please try again later.";
    }
  };

  const saveGlobalSettings = async (settings: Partial<GlobalSettings>) => {
    if (!db) return;
    try {
      const newSettings = { ...globalSettings, ...settings };
      
      // Separate sensitive backend credentials from public configurations
      const publicSettings: Record<string, any> = { ...newSettings };
      const adminSecretSettings: Record<string, any> = {};

      const sensitiveKeys: (keyof GlobalSettings)[] = ['stripeSecretKey', 'paypalSecret', 'smtpPass'];
      for (const key of sensitiveKeys) {
        if (publicSettings[key] !== undefined) {
          adminSecretSettings[key] = publicSettings[key];
          delete publicSettings[key];
        }
      }

      await setDoc(doc(db, "system", "config"), publicSettings, { merge: true });

      if (Object.keys(adminSecretSettings).length > 0 && user?.role === 'admin') {
        await setDoc(doc(db, "system", "admin_config"), adminSecretSettings, { merge: true });
      }

      // If SMTP was configured by admin, sync to backend in-memory mail relay cache
      if (user?.role === 'admin' && (newSettings.smtpHost || adminSecretSettings.smtpPass)) {
        try {
          const authHeaders = await getAuthHeaders();
          await fetch(`${API_BASE_URL}/api/admin/smtp-config`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              smtpHost: newSettings.smtpHost,
              smtpPort: newSettings.smtpPort,
              smtpUser: newSettings.smtpUser,
              smtpPass: newSettings.smtpPass || adminSecretSettings.smtpPass
            })
          });
        } catch (smtpSyncErr) {
          console.debug("SMTP server cache sync notice:", smtpSyncErr);
        }
      }

      setGlobalSettings(newSettings);
      await logAction("Update Global Settings");
    } catch (e) {
      console.error("Sync Error: Failed to commit global configurations.", e);
    }
  };

  const sendContactMessage = async (data: { name: string, surname: string, email: string, phone: string, message: string, isRegistered: boolean, mrNumber?: string }) => {
    if (!db) return;

    const dedupeKey = `${(data.email || '').toLowerCase().trim()}_${(data.message || '').toLowerCase().trim()}`;
    const now = Date.now();
    const lastSent = contactDedupeRef.current.get(dedupeKey);
    if (lastSent && (now - lastSent) < 10000) {
      console.log("[AppContext] Duplicate contact message dispatch prevented.");
      return;
    }
    contactDedupeRef.current.set(dedupeKey, now);

    try {
      const messageData = {
        ...data,
        timestamp: new Date().toISOString(),
        status: 'new'
      };
      await addDoc(collection(db, "inquiries"), sanitizeData(messageData));
      
      // Dispatch via backend contact endpoint (handles both notification to info@rovita.co and auto-reply to sender)
      try {
        const response = await fetch(`${API_BASE_URL}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...messageData,
            smtpConfig: (globalSettings.smtpHost && globalSettings.smtpUser && globalSettings.smtpPass) ? {
              smtpHost: globalSettings.smtpHost,
              smtpPort: globalSettings.smtpPort,
              smtpUser: globalSettings.smtpUser,
              smtpPass: globalSettings.smtpPass
            } : undefined
          })
        });

        if (!response.ok) {
          console.warn("API contact endpoint returned status:", response.status);
        }
      } catch (e) {
        console.debug("API contact request completed or skipped.");
      }

      if (user) await logAction("Sent Contact Message");
    } catch (e) {
      console.error("Contact form error", e);
      throw e;
    }
  };

  const replyToContactMessage = async (id: string, email: string, reply: string) => {
    if (!db) return;
    
    // 1. Send Email via platform relay
    const subject = "Response to Your Inquiry - ROVITA Virtual Clinic";
    const body = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Response from ROVITA Support</title>
      </head>
      <body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #1e293b;">
        <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f8fafc; padding: 24px 0;">
          <tr>
            <td align="center">
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
                <tr>
                  <td style="padding: 32px 32px 24px 32px; background: linear-gradient(135deg, #008080, #0f766e); text-align: center;">
                    <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">ROVITA Virtual Clinic</h1>
                    <p style="margin: 6px 0 0 0; color: #ccfbf1; font-size: 13px; font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px;">Clinical & Administrative Support</p>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 32px;">
                    <p style="font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">Dear Patient,</p>
                    <p style="font-size: 15px; line-height: 1.6; color: #475569; margin: 0 0 20px 0;">
                      Thank you for contacting ROVITA Virtual Clinic. Our medical administration team has reviewed your inquiry and provided the following response:
                    </p>
                    <div style="background-color: #f0fdfa; border-left: 4px solid #008080; padding: 18px; border-radius: 8px; margin: 20px 0; font-size: 15px; line-height: 1.6; color: #1e293b;">
                      ${reply.replace(/\n/g, '<br/>')}
                    </div>
                    <p style="font-size: 14px; line-height: 1.6; color: #334155; margin-bottom: 24px;">
                      If you have any further questions or require additional assistance, please reply to this email or visit your ROVITA portal.
                    </p>
                    <p style="font-size: 14px; line-height: 1.6; color: #64748b; margin: 0;">
                      Warm regards,<br />
                      <strong style="color: #1e293b;">ROVITA Clinical Administration Team</strong>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 20px 32px; background-color: #f8fafc; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #94a3b8; line-height: 1.5;">
                    <p style="margin: 0 0 4px 0;">ROVITA Virtual Clinic • Healthcare Support Communication</p>
                    <p style="margin: 0;">Inquiries: <a href="mailto:info@rovita.co" style="color: #008080; text-decoration: none;">info@rovita.co</a></p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;

    await sendSystemEmail(email, subject, body);

    // 2. Update record in Firestore
    await updateDoc(doc(db, "inquiries", id), {
      status: 'read',
      repliedAt: new Date().toISOString(),
      replyMessage: reply
    });

    await logAction(`Replied to Inquiry: ${id}`);
  };

  const deleteContactMessage = async (id: string) => {
    if(!db) return;
    await deleteDoc(doc(db, "inquiries", id));
    await logAction(`Delete Inquiry: ${id}`);
  };

  const updateContactMessage = async (id: string, status: 'new' | 'read') => {
    if(!db) return;
    await updateDoc(doc(db, "inquiries", id), { status });
    await logAction(`Marked Inquiry as ${status}: ${id}`);
  };

  const submitReview = async (doctorId: string, rating: number, comment: string) => {
    if (!user) return;
    const reviewData: Review = {
        id: '', // Placeholder, ignored by addDoc
        doctorId,
        patientId: user.id,
        patientName: user.name,
        rating,
        comment,
        timestamp: new Date().toISOString()
    };
    // Use sanitizeData to ensure clean payload
    await addDoc(collection(db, "reviews"), sanitizeData(reviewData));
    await logAction(`Submit Review: Doctor ${doctorId}`);
  };

  const createPaymentIntent = async (amount: number) => {
    if (!globalSettings.stripeEnabled || !globalSettings.stripeSecretKey) {
        throw new Error("Payment gateway not configured.");
    }
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE_URL}/api/payments/create-intent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
            amount, 
            currency: globalSettings.platformCurrency.toLowerCase(),
            gatewayConfig: { stripeSecretKey: globalSettings.stripeSecretKey }
        })
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.clientSecret;
  };

  const testSmtpConfig = async (config: { smtpHost?: string; smtpPort?: string; smtpUser?: string; smtpPass?: string; testRecipient?: string }) => {
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/test-smtp`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(config)
      });
      const data = await res.json();
      return data;
    } catch (err: any) {
      return { success: false, error: err.message || 'Network connection failed' };
    }
  };

  return <AppContext.Provider value={{ 
    user, loading, doctors, doctorsError, allUsers, appointments, allAppointments, medicalRecords, notifications, auditLogs, contactMessages, reviews, globalSettings,
    userCurrency, userCountry, toasts, showToast, removeToast, setUserCurrency, formatDoctorPrice, toggleDoctorBusySlot, setDoctorDayBusy, 
    login, loginGuest, linkGuestAccount, loginDoctorGuest, loginAdminGuest, switchUserRoleDemo, logout, register, adminCreateUser, bookAppointment, updateAppointmentStatus, cancelAppointment, updateUserProfile, manageUser, toggleUserDisabled, deleteUser, resetUserPassword, changeUserPassword, adminChangeUserPassword, addMedicalRecord, updateMedicalRecord, deleteMedicalRecord, issueDigitalPrescription, uploadClinicalFile, uploadPublicFile, submitTriageQuestionnaire, clearNotification, askClinicalAI, saveGlobalSettings, adminUpdateAppointment, adminDeleteAppointment, scheduleFollowUp, referPatient, sendContactMessage, updateContactMessage, replyToContactMessage, deleteContactMessage, submitReview, createPaymentIntent, sendSystemEmail, checkAndDispatchAppointmentReminders, testSmtpConfig
  }}>
    {children}
    <ToastContainer toasts={toasts} onRemove={removeToast} />
  </AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};